import React, { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import ReactDOM from 'react-dom/client';
import AnimatedHeartMarker from './AnimatedHeartMarker';
import {
  shouldRefire,
  buildFireworksSchedule,
  pickSprinkle,
  HeartSpawn,
  featureLikeCount,
  trimToLikeBudget,
} from './heartAnimation';
import mapboxgl, { MapMouseEvent } from 'mapbox-gl';
import polylabel from '@mapbox/polylabel';
import TagManager from 'react-gtm-module';
import 'mapbox-gl/dist/mapbox-gl.css';
import { BuildingWithConvertedAddresses, LatLng } from '../utils/types';
import { getBuildingLikeCoordinates } from '../utils/buildingLike';
import { getCookie } from '../utils/utils';
import { distanceMeters } from '../utils/geo';
import './HouseMap.scss';
import { performReverseGeocodingAndFetchPOIs, reverseGeocodeNearestAddress } from './BuildingMap.helpers';
import { fetchSideAddresses, resolvePrimaryAddress } from './sideAddresses';
import { fetchSideAddressesWithDeadline } from './fetchSideAddressesWithDeadline';
import { footprintFromRenderedMap } from './footprintFromRenderedMap';
import { fetchPlaceLabels, NearbyPlaceLabel } from './placeLabels';
import { recordBuildingCentroid } from './recordBuildingCentroid';
import { COIN_SWITCH_ZOOM, demandCoinAt, stageForWeight } from './demandLayers';
import { domMarkerFallbackEnabled } from '../utils/gpuQuirks';
import { attachDomMarkerFallback } from './domMarkerFallback';
import { whenMapStyleReady } from './whenMapStyleReady';
import {
  loadSeenNewBuildings,
  markBuildingSeen,
  setServerSeenBuildings,
} from './seenBuildings';
import LoadingSpinner from '../common/LoadingSpinner';
import RightArrow from '../AppContent/RightArrow';
import { aggregateBuildingFeatures } from '../AppContent/NewLandingPage/aggregateBuildingFeatures';
import { Trans, useTranslation } from 'react-i18next';
interface MapBoxExampleProps {
  mapCenter: LatLng;
  selectedBuilding: BuildingWithConvertedAddresses | null;
  triggeredFromSearch: boolean;
  searchResultBounds?: { sw: [number, number]; ne: [number, number]; maxZoom?: number } | null;
  feedIsOpen: boolean;
  mobileFeedIsOpen: boolean;
  countryOfFeedContent: string;
  lastMapBoundaryBoxCenter: LatLng | null;
  lastZoomLevel: number | null;
  initialMapZoom: number;
  isLandingPage?: boolean;
  isSmallMap?: boolean;
  /** Open the on-map preview the instant a LISTING marker is clicked, before
   *  reverse-geocoding resolves (the preview seeds from already-loaded data).
   *  Any other building opens once the geocode answers: with the geocoder's
   *  first address or, for a plain building or dot with no address at all, its
   *  district alone. A plain building stays closed only when there is no
   *  district and no municipality name. Where this prop is off, a listing
   *  marker opens with the first address, or never when there is none. Only the
   *  NewLandingPage preview flow opts in; other consumers route the click to
   *  the full building view, which must wait for the enriched object. */
  openPreviewOnClick?: boolean;
  /** The consumer's selection token (useBuildingPreview's selectionRef). The map
   *  advances it on every click and on unmount and passes the click's value to
   *  selectBuilding, so the consumer can drop a callback once anything newer
   *  (another click, a close, an outside open) has advanced it. Without it the
   *  map keeps a private counter that only newer clicks and unmount advance. */
  selectionRef?: React.MutableRefObject<number>;
  hideGeolocateControl?: boolean;
  // When true, clicking a building/listing does nothing (no building page, no
  // "mark seen") and hover doesn't show the pointer cursor. Used by the
  // display-only "Add my place" address map.
  disableBuildingClick?: boolean;
  // When true, the map is fully static: no pan, zoom, rotate, pitch, or keyboard
  // interaction (Mapbox `interactive: false`). Used by the "Add my place"
  // address step so the map reads as a frozen display.
  staticMap?: boolean;
  // "Add my place" building step: highlight the complete footprint of the
  // building at this coordinate (teal, like the landing-page hover) and anchor
  // `addressCallout` to it. Clicking another building re-targets both.
  addressHighlightAt?: LatLng | null;
  addressCallout?: React.ReactNode;
  /** "Add my place" building step: a click started resolving another building's
   *  address, so the caller's current selection is stale until `selectBuilding`
   *  delivers the new one. */
  onBuildingPickStart?: () => void;
  /** "Add my place" building step: the pick announced by `onBuildingPickStart`
   *  found no address, or its lookup failed, so `selectBuilding` will not
   *  follow. Only the latest pick reports (JAA-410). */
  onBuildingPickFailed?: () => void;
  // When true, hide all market-activity clutter: buy/sell listing markers,
  // like hearts, and building dots. Used by the Add-my-place flow maps.
  simplifiedMap?: boolean;
  // When true, hide base-map text labels such as streets, places, and POIs.
  hideLabels?: boolean;
  // Sell-marker decluttering (JAA sell-listing map marker change): promising
  // sell listings keep the full house pill, low-content ones render as a small
  // white dot, and Mapbox collision hides whichever markers would overlap —
  // best-ranked first via symbol-sort-key, more appearing as the user zooms
  // in. Only the landing page opts in; other consumers keep every pill
  // visible. Captured at map creation — not runtime-togglable.
  declutterSellMarkers?: boolean;
  // When true, recenter on `mapCenter` changes instantly (jumpTo) instead of
  // animating (flyTo). Used by the Add-my-place flow so moving to the picked
  // building has no slow camera pan.
  recenterInstant?: boolean;
  // Zoom applied alongside an instant recenter; defaults to the current zoom.
  recenterZoom?: number;
  offMarketFeatureCollection?: GeoJSON.FeatureCollection | null;
  onUserMapDragEnd?: () => void;
  onUserMapInteraction?: () => void;
  onMapMoveStart?: () => void;
  /** The movement that just started turned out to move nothing — a container
   * resize, not a camera move. Lets the caller undo whatever onMapMoveStart put
   * on screen, since no fetch will follow to clear it. */
  onMapMoveDiscarded?: () => void;
  onGeolocateStart?: () => void;
  onGeolocateEnd?: () => void;
  /** Called once with the raw map after 'load'. Lets a consumer attach custom
   *  sources/layers (e.g. the JAA-282 area-interest demand overlay). */
  onMapReady?: (map: mapboxgl.Map) => void;
  onVisibleCountsChange?: (counts: { sellers: number; buyers: number }) => void;
  onVisibleBoundsChange?: (bounds: { sw: [number, number]; ne: [number, number] }) => void;
  setShowFeed: (showFeed: boolean) => void;
  setTriggeredFromSearch: (triggeredFromSearch: boolean) => void;
  selectBuilding: (
    building: BuildingWithConvertedAddresses,
    point?: { x: number; y: number },
    selectionToken?: number,
  ) => void;
  setCountryOfFeedContent: (country: string) => void;
  setLastMapBoundaryBoxCenter: (lastMapBoundaryBoxCenter: LatLng) => void;
  setLastZoomLevel: (lastZoomLevel: number) => void;
}

const GEOLOCATE_TARGET_ZOOM = 16;
const APPROXIMATE_LOCATION_THRESHOLD_METERS = 1000;
const GEOLOCATE_HINT_HIDE_DELAY_MS = 10000;

// Zoom at which the clickable building dots appear (the "every building is
// clickable" affordance, mobile-only). Shared threshold: at this zoom the
// sell-marker decluttering also stops — every sell listing renders as a full
// pill, because a reduced dot at building-inspection zoom hides information
// for no gain. FlowMap reads it too so the Add-my-place maps open dots-deep.
export const BUILDING_DOTS_MIN_ZOOM = 16;

const isIosChrome = () => {
  if (typeof navigator === 'undefined') return false;

  const userAgent = navigator.userAgent;
  return /CriOS/i.test(userAgent) && /iPad|iPhone|iPod/i.test(userAgent);
};

const hideTextLabelLayers = (map: mapboxgl.Map, hiddenIds?: Set<string>): void => {
  const style = map.getStyle();
  if (!style.layers) return;

  style.layers.forEach((layer) => {
    const layout = (layer as { layout?: Record<string, unknown> }).layout;
    if (layer.type !== 'symbol' || layout?.['text-field'] === undefined) return;
    if (layout.visibility === 'none') return;

    try {
      map.setLayoutProperty(layer.id, 'visibility', 'none');
      hiddenIds?.add(layer.id);
    } catch {
      // Ignore style races while Mapbox is still resolving layers.
    }
  });
};

// Re-show only the text label layers we previously hid (so layers that were
// `none` in the base style stay hidden). Used when toggling `hideLabels` off.
const restoreTextLabelLayers = (map: mapboxgl.Map, hiddenIds: Set<string>): void => {
  hiddenIds.forEach((id) => {
    try {
      if (map.getLayer(id)) {
        map.setLayoutProperty(id, 'visibility', 'visible');
      }
    } catch {
      // Ignore style races while Mapbox is still resolving layers.
    }
  });
  hiddenIds.clear();
};

// Mapbox has no runtime `setInteractive`, so freeze/unfreeze by flipping each
// gesture handler. Lets a map created `interactive` become static (and back)
// without recreating the WebGL context.
const setMapInteractive = (map: mapboxgl.Map, interactive: boolean): void => {
  const handlers = [
    map.dragPan,
    map.scrollZoom,
    map.boxZoom,
    map.dragRotate,
    map.keyboard,
    map.doubleClickZoom,
    map.touchZoomRotate,
    map.touchPitch,
  ];
  handlers.forEach((handler) => {
    try {
      if (interactive) handler?.enable();
      else handler?.disable();
    } catch {
      // Some handlers may be unavailable depending on map options; ignore.
    }
  });
};

export type BuildingMapHandle = {
  triggerGeolocate: () => void;
  zoomIn: () => void;
  zoomOut: () => void;
  /** Fly the map to a centre (optionally at a zoom). Used after the could-buy
   *  flow completes to frame the building or area the buyer just created. */
  flyTo: (center: { lat: number; lng: number }, zoom?: number) => void;
  refreshListings: () => void;
  playLikeAnimation: (building: BuildingWithConvertedAddresses) => void;
  /**
   * Centre the map on a building and report its on-screen (viewport) point once
   * settled, so callers (e.g. the explore list) can anchor the on-map preview
   * to the building instead of docking it over the list panel.
   */
  focusBuilding: (
    building: BuildingWithConvertedAddresses,
    onAnchored: (point: { x: number; y: number } | null) => void,
  ) => void;
  /** Project a lng/lat to its current on-screen (viewport) point, or null if the
   *  map isn't ready. Used to keep the on-map preview card glued to its building
   *  as the map pans/zooms. */
  projectLngLat: (coords: { lat: number; lng: number }) => { x: number; y: number } | null;
  /** Subscribe to map movement (pan/zoom/resize). Returns an unsubscribe fn.
   *  Lets the preview card re-project on every frame without re-rendering React. */
  subscribeMove: (cb: () => void) => () => void;
  /** Keep a building visually selected (green polygon + selected sell icon +
   *  selected dot) while its preview card is open. Pass null to clear. */
  highlightBuilding: (buildingId: string | null) => void;
  /** Settlement-subdivision labels (kaupunginosa-level place labels) within
   *  `radiusM` of a point, nearest first. Asks the Tilequery API rather than
   *  reading the rendered tiles, so the answer does not depend on the current
   *  zoom or on which tiles happen to be loaded. If Tilequery is unreachable it
   *  falls back to the loaded street tiles, waiting for the map to settle when
   *  the point lies outside the loaded area or the style is still loading, and
   *  resolves [] if the map is removed meanwhile. */
  nearbyPlaceLabels: (center: { lat: number; lng: number }, radiusM: number) => Promise<NearbyPlaceLabel[]>;
};

// How long to wait for the map to settle before reading labels anyway.
const PLACE_LABELS_SETTLE_MS = 4000;

// Resolve on the map's next `idle`, or after `ms` if nothing is pending.
const waitForIdle = (map: mapboxgl.Map, ms: number): Promise<void> =>
  new Promise((resolve) => {
    const timer = window.setTimeout(() => {
      map.off('idle', done);
      resolve();
    }, ms);
    const done = () => {
      window.clearTimeout(timer);
      resolve();
    };
    map.once('idle', done);
  });

// Read settlement-subdivision labels out of the loaded street tiles, deduped by
// name (a label repeats across tile borders), nearest to `center` first. Kept as
// the fallback for when the Tilequery request fails.
const collectPlaceLabels = (map: mapboxgl.Map, center: LatLng, radiusM: number): NearbyPlaceLabel[] => {
  const features = map.querySourceFeatures('composite', {
    sourceLayer: 'place_label',
    filter: ['==', ['get', 'class'], 'settlement_subdivision'],
  });
  const byName = new Map<string, NearbyPlaceLabel>();
  features.forEach((feature) => {
    if (feature.geometry.type !== 'Point') return;
    const [lng, lat] = feature.geometry.coordinates as [number, number];
    const name = String(feature.properties?.name ?? '').trim();
    const kind = String(feature.properties?.type ?? '');
    if (!name || kind === 'block') return;
    const distanceM = distanceMeters(center, { lat, lng });
    if (distanceM > radiusM) return;
    const seen = byName.get(name);
    if (!seen || distanceM < seen.distanceM) byName.set(name, { name, kind, lat, lng, distanceM });
  });
  return Array.from(byName.values()).sort((a, b) => a.distanceM - b.distanceM);
};

/** The shared Mapbox building map: listings, hover and selection, and the Add-my-place building picker. */
const BuildingMap = forwardRef<BuildingMapHandle, MapBoxExampleProps>(({
  mapCenter,
  triggeredFromSearch,
  searchResultBounds,
  selectedBuilding,
  feedIsOpen,
  mobileFeedIsOpen,
  countryOfFeedContent,
  lastMapBoundaryBoxCenter,
  lastZoomLevel,
  isLandingPage,
  isSmallMap,
  openPreviewOnClick,
  selectionRef,
  hideGeolocateControl,
  disableBuildingClick,
  staticMap,
  addressHighlightAt,
  addressCallout,
  onBuildingPickStart,
  onBuildingPickFailed,
  simplifiedMap,
  hideLabels,
  declutterSellMarkers,
  recenterInstant,
  recenterZoom,
  offMarketFeatureCollection,
  onUserMapDragEnd,
  onUserMapInteraction,
  onMapMoveStart,
  onMapMoveDiscarded,
  onGeolocateStart,
  onGeolocateEnd,
  onMapReady,
  onVisibleCountsChange,
  onVisibleBoundsChange,
  setShowFeed,
  selectBuilding,
  setTriggeredFromSearch,
  setCountryOfFeedContent,
  setLastMapBoundaryBoxCenter,
  setLastZoomLevel,
  initialMapZoom,
}, ref) => {
  const { t, i18n } = useTranslation();
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  // The area-interest coin tooltip (JAA-329 #16): one shared popup, shown on
  // desktop hover / mobile tap of a stage coin.
  const areaTooltipRef = useRef<mapboxgl.Popup | null>(null);
  const speechBubbleRef = useRef<HTMLDivElement>(null);
  const jaakkoInfoRef = useRef<HTMLDivElement>(null);
  const styleUrlRef = useRef<string>('');

  const hoverStateRef = useRef<HoverState>({ buildingId: null });
  // A building "pinned" highlighted while its preview card is open. Reuses the
  // hover feature-state (green polygon + selected sell icon + selected dot) but
  // is immune to mousemove clearing and re-applied after moveend setData wipes.
  const pinnedBuildingRef = useRef<string | null>(null);

  const heartMarkersRef = useRef<
    Map<string, { marker: mapboxgl.Marker; root: ReactDOM.Root }>
  >(new Map());
  const recentLikesRef = useRef<GeoJSON.FeatureCollection | null>(null);
  // The lone all-time fallback heart currently on screen (building id + when it
  // was spawned). Lets duplicate fetches for the same empty area leave the heart
  // playing instead of tearing it down and replaying it ("twice in a row").
  const heartScheduleRef = useRef<{
    nextTimer: number | null;
    deathTimers: Set<number>;
  }>({ nextTimer: null, deathTimers: new Set() });
  // Last viewport that actually fired a heart burst — basis for the
  // "moved enough to be a new area" threshold (JAA-254).
  const lastFiredViewRef = useRef<{ center: mapboxgl.LngLat; zoom: number } | null>(
    null,
  );
  const heartIdleTimerRef = useRef<number | null>(null);
  const styleRestoreTimerRef = useRef<number | null>(null);
  const playLikeAnimationRef = useRef<
    ((building: BuildingWithConvertedAddresses) => void) | null
  >(null);

  const prevSelectedBuildingRef = useRef<BuildingWithConvertedAddresses | null>(
    null,
  );
  const selectBuildingRef = useRef(selectBuilding);
  const onBuildingPickStartRef = useRef(onBuildingPickStart);
  const onBuildingPickFailedRef = useRef(onBuildingPickFailed);
  const onUserMapDragEndRef = useRef(onUserMapDragEnd);
  const onUserMapInteractionRef = useRef(onUserMapInteraction);
  const onMapMoveStartRef = useRef(onMapMoveStart);
  const onMapMoveDiscardedRef = useRef(onMapMoveDiscarded);
  const onGeolocateStartRef = useRef(onGeolocateStart);
  const onMapReadyRef = useRef(onMapReady);
  const onGeolocateEndRef = useRef(onGeolocateEnd);
  const onVisibleCountsChangeRef = useRef(onVisibleCountsChange);
  const onVisibleBoundsChangeRef = useRef(onVisibleBoundsChange);
  // Latest off-market collection + simplified flag, mirrored into refs so the
  // style-reload restore handler (a long-lived `styledata` listener) can
  // re-apply the authoritative parent-owned data without re-subscribing.
  const offMarketFeatureCollectionRef = useRef(offMarketFeatureCollection);
  const simplifiedMapRef = useRef(simplifiedMap);
  const geolocateControlRef = useRef<mapboxgl.GeolocateControl | null>(null);
  const explicitGeolocateRequestRef = useRef(false);

  const finishExplicitGeolocateRequest = () => {
    if (!explicitGeolocateRequestRef.current) return;
    explicitGeolocateRequestRef.current = false;
    onGeolocateEndRef.current?.();
  };

  useImperativeHandle(ref, () => ({
    /** Tilequery first. In the rendered-tile fallback a fresh map reports its
     *  empty tiles as loaded, so the style check makes it wait; a map removed
     *  mid-wait yields [] rather than throwing in querySourceFeatures. */
    nearbyPlaceLabels: async (center, radiusM) => {
      const fetched = await fetchPlaceLabels(center, radiusM);
      if (fetched) return fetched;
      // Tilequery unreachable (offline, no token, rate limit): fall back to the
      // rendered tiles, which is what this used to read exclusively.
      const map = mapRef.current;
      if (!map) return [];
      const outside = !map.getBounds()?.contains([center.lng, center.lat]);
      if (outside || map.isMoving() || !map.isStyleLoaded() || !map.areTilesLoaded()) {
        await waitForIdle(map, PLACE_LABELS_SETTLE_MS);
        if (mapRef.current !== map) return [];
      }
      return collectPlaceLabels(map, center, radiusM);
    },
    triggerGeolocate: () => {
      const geolocateControl = geolocateControlRef.current;
      if (!geolocateControl) return;
      explicitGeolocateRequestRef.current = true;
      onGeolocateStartRef.current?.();
      geolocateControl.trigger();
    },
    zoomIn: () => {
      mapRef.current?.zoomIn();
    },
    zoomOut: () => {
      mapRef.current?.zoomOut();
    },
    flyTo: (center, zoom) => {
      mapRef.current?.flyTo({
        center: [center.lng, center.lat],
        ...(zoom != null ? { zoom } : {}),
      });
    },
    refreshListings: () => {
      // Re-fetch off-market listings for the current viewport and re-set the
      // clicked-buildings source. Called after add/remove willingness so the
      // marker count updates without waiting for the next moveend.
      if (!mapRef.current) return;
      try {
        fetchBuildingsWithinBounds(mapRef, (counts) =>
          onVisibleCountsChangeRef.current?.(counts),
        );
      } catch (err) {
        console.error('refreshListings failed', err);
      }
    },
    playLikeAnimation: (building) => {
      playLikeAnimationRef.current?.(building);
    },
    focusBuilding: (building, onAnchored) => {
      const map = mapRef.current;
      const coords =
        building.centroid ||
        building.addresses?.[0]?.coordinates ||
        building.properties?.addresses?.[0]?.coordinates;
      if (!map || !coords) {
        onAnchored(null);
        return;
      }
      const center: [number, number] = [coords.lng, coords.lat];
      const rect = map.getContainer().getBoundingClientRect();
      const anchorNow = () => {
        const p = map.project(center);
        onAnchored({ x: rect.left + p.x, y: rect.top + p.y });
      };
      /** Is the building actually painted on the canvas right now? */
      const isOnScreen = () => {
        const p = map.project(center);
        return p.x >= 0 && p.y >= 0 && p.x <= rect.width && p.y <= rect.height;
      };
      // Already visible: don't move the map. A recenter would only reload the
      // master list for no benefit (the preview card tracks the building
      // wherever it sits). Visibility is about where the building is, not how
      // far the camera happens to be zoomed in — a tab opened straight onto a
      // listing sits at the default zoom with the building possibly hundreds of
      // kilometres away, so the zoom test alone let it stay off-screen. Asked of
      // the projected pixel rather than getBounds(), which is an axis-aligned
      // envelope and reports a rotated or pitched map as containing points that
      // are not on the canvas.
      const FOCUS_FLY_ZOOM = 15;
      if (map.getZoom() >= FOCUS_FLY_ZOOM && isOnScreen()) {
        anchorNow();
        return;
      }
      // Low zoom: the building is small/far — fly in so it's actually visible.
      let done = false;
      const finish = () => {
        if (done) return;
        done = true;
        anchorNow();
      };
      map.once('moveend', finish);
      map.flyTo({ center, zoom: FOCUS_FLY_ZOOM, duration: 500 });
      // Fallback if the camera barely moves (no moveend) or it stalls.
      setTimeout(finish, 650);
    },
    projectLngLat: (coords) => {
      const map = mapRef.current;
      if (!map || !coords) return null;
      const rect = map.getContainer().getBoundingClientRect();
      const p = map.project([coords.lng, coords.lat]);
      return { x: rect.left + p.x, y: rect.top + p.y };
    },
    subscribeMove: (cb) => {
      const map = mapRef.current;
      if (!map) return () => {};
      map.on('move', cb);
      map.on('zoom', cb);
      map.on('resize', cb);
      return () => {
        map.off('move', cb);
        map.off('zoom', cb);
        map.off('resize', cb);
      };
    },
    highlightBuilding: (buildingId) => {
      const prev = pinnedBuildingRef.current;
      pinnedBuildingRef.current = buildingId;
      const map = mapRef.current;
      if (!map) return;
      // Clear the previous pin's hover state, unless the mouse is currently
      // hovering it (let the mousemove handler own that one).
      if (prev && prev !== buildingId && prev !== hoverStateRef.current.buildingId) {
        setHoverByBuildingId(map, null, prev);
      }
      if (buildingId) {
        setHoverByBuildingId(map, buildingId, null);
      }
    },
  }), []);

  const [showZoomHint, setShowZoomHint] = useState<boolean>(() => {
    const storedValue = localStorage.getItem('showZoomHint');
    return storedValue === null ? true : storedValue === 'true';
  });

  const [showClickBuildingHint, setShowClickBuildingHint] = useState<boolean>(
    () => {
      const storedValue = localStorage.getItem('showClickBuildingHint');
      return storedValue === null ? true : storedValue === 'true';
    },
  );

  useEffect(() => {
    selectBuildingRef.current = selectBuilding;
  }, [selectBuilding]);

  useEffect(() => {
    onBuildingPickStartRef.current = onBuildingPickStart;
  }, [onBuildingPickStart]);

  useEffect(() => {
    onBuildingPickFailedRef.current = onBuildingPickFailed;
  }, [onBuildingPickFailed]);

  const disableBuildingClickRef = useRef(disableBuildingClick);
  useEffect(() => {
    disableBuildingClickRef.current = disableBuildingClick;
  }, [disableBuildingClick]);

  // `staticMap` is applied at construction (`interactive: !staticMap`); toggle
  // the gesture handlers at runtime so the shared Add-my-place map can switch
  // between the frozen address step and the interactive building step.
  const staticMapRef = useRef(staticMap);
  useEffect(() => {
    staticMapRef.current = staticMap;
    const map = mapRef.current;
    if (map) setMapInteractive(map, !staticMap);
  }, [staticMap]);

  // Ids of text label layers hidden by `hideLabels`, so toggling it back on
  // restores exactly those (and not layers that were `none` in the base style).
  const hiddenLabelLayerIdsRef = useRef<Set<string>>(new Set());
  const hideLabelsRef = useRef(hideLabels);
  useEffect(() => {
    hideLabelsRef.current = hideLabels;
    const map = mapRef.current;
    if (!map) return;
    if (hideLabels) hideTextLabelLayers(map, hiddenLabelLayerIdsRef.current);
    else restoreTextLabelLayers(map, hiddenLabelLayerIdsRef.current);
  }, [hideLabels]);

  // Read inside the `mapCenter` recenter effect (which only depends on
  // `mapCenter`) so the instant-jump behavior always uses current values.
  const recenterInstantRef = useRef(recenterInstant);
  const recenterZoomRef = useRef(recenterZoom);
  useEffect(() => {
    recenterInstantRef.current = recenterInstant;
    recenterZoomRef.current = recenterZoom;
  }, [recenterInstant, recenterZoom]);

  // ── "Add my place" building highlight + geo-anchored callout ──────────
  const addressHighlightAtRef = useRef(addressHighlightAt);
  const addressCalloutRef = useRef(addressCallout);
  const calloutMarkerRef = useRef<mapboxgl.Marker | null>(null);
  const calloutRootRef = useRef<ReactDOM.Root | null>(null);

  // Add the dedicated highlight overlay (a GeoJSON source + teal fill/outline,
  // matching the landing-page hover style). Kept separate from feature-state
  // hover so the persistent selection doesn't fight the live cursor hover.
  const ensureHighlightLayers = useRef(() => {
    const map = mapRef.current;
    // The first manual pick has geometry before its address lookup finishes.
    if (!map || (!addressHighlightAtRef.current && !onBuildingPickStartRef.current)) return;
    if (!map.getSource('amp-building-highlight')) {
      map.addSource('amp-building-highlight', {
        type: 'geojson',
        data: { type: 'FeatureCollection', features: [] },
      });
    }
    if (!map.getLayer('amp-building-highlight-fill')) {
      map.addLayer({
        id: 'amp-building-highlight-fill',
        type: 'fill',
        source: 'amp-building-highlight',
        paint: { 'fill-color': '#00805A', 'fill-opacity': 0.15 },
      });
    }
    if (!map.getLayer('amp-building-highlight-outline')) {
      map.addLayer({
        id: 'amp-building-highlight-outline',
        type: 'line',
        source: 'amp-building-highlight',
        paint: { 'line-color': '#00805A', 'line-width': 2 },
      });
    }
  });

  // Re-apply the off-market listings source + icon layers after a style reload.
  // setStyle() wipes every custom source/layer (including `clicked-buildings`),
  // and the recurring `styledata` handler that re-adds hover/highlight layers
  // never restored these — so the buy/sell icons stayed gone until a full page
  // refresh. Hearts survived because they are HTML markers, not style layers.
  //
  // Wired into the long-lived `styledata` listener so ANY reload recovers them.
  // Guarded with a health check so benign `styledata` ticks (source + layers
  // already present) are no-ops, which also prevents a re-fetch storm on
  // self-owned maps where re-population is asynchronous.
  const restoreOffMarketLayers = useRef(() => {
    const map = mapRef.current;
    if (!map || !map.isStyleLoaded()) return;
    // Simplified (Add-my-place) maps never create the off-market source/layers.
    if (simplifiedMapRef.current) return;

    const hasSource = !!map.getSource('clicked-buildings');
    const hasJaakkoOutlineLayer = !!map.getLayer('building-jaakko-outline');
    const hasSellLayer = !!map.getLayer('building-off-market-sell-icon');
    const hasSelectedSellLayer = !!map.getLayer('building-off-market-sell-icon-selected');
    const hasBuyLayer = !!map.getLayer('building-off-market-buy-icon');
    // Decluttered maps also need the sell-dot layer restored after a reload.
    const hasSellDotLayer =
      !isSellDeclutterEnabled(map) ||
      !!map.getLayer('building-off-market-sell-dot');
    // Healthy — nothing was dropped, so don't touch the source/layers.
    if (
      hasSource &&
      hasJaakkoOutlineLayer &&
      hasSellLayer &&
      hasSelectedSellLayer &&
      hasSellDotLayer &&
      hasBuyLayer
    ) return;

    if (hasSource) {
      // Source present but the icon layers are missing — either dropped by a
      // style reload or still being added during the initial load window. Just
      // (re-)add the layers; do NOT re-setData, otherwise this re-runs on every
      // styledata tick (each addImage/addLayer emits one) and churns the source.
      ensureClickedBuildingLayers(mapRef);
      return;
    }

    // Source was wiped (style reload) — recreate it from the authoritative data.
    if (offMarketFeatureCollectionRef.current !== undefined) {
      // Parent-owned (landing page): re-apply the prop data.
      upsertClickedBuildingsSource(
        mapRef,
        offMarketFeatureCollectionRef.current || {
          type: 'FeatureCollection',
          features: [],
        },
      );
    } else {
      // Self-owned map: create the source synchronously (so this guard
      // short-circuits on follow-up `styledata` ticks and avoids a fetch
      // storm), then refill it from the network.
      upsertClickedBuildingsSource(mapRef, {
        type: 'FeatureCollection',
        features: [],
      });
      fetchBuildingsWithinBounds(mapRef, (counts) =>
        onVisibleCountsChangeRef.current?.(counts),
      );
    }
  });

  const positionCallout = useRef((lngLat: [number, number]) => {
    const map = mapRef.current;
    if (!map) return;
    if (!calloutMarkerRef.current) {
      const el = document.createElement('div');
      el.className = 'amp-building__marker';
      const root = ReactDOM.createRoot(el);
      root.render(<>{addressCalloutRef.current}</>);
      calloutRootRef.current = root;
      calloutMarkerRef.current = new mapboxgl.Marker({ element: el, anchor: 'bottom' })
        .setLngLat(lngLat)
        .addTo(map);
    } else {
      calloutMarkerRef.current.setLngLat(lngLat);
    }
  });

  // Draw a footprint into the highlight overlay and move the callout to it.
  const applyHighlightGeometry = useRef((geometry: GeoJSON.Geometry | null) => {
    const map = mapRef.current;
    if (!map) return;
    ensureHighlightLayers.current();
    const src = map.getSource('amp-building-highlight') as mapboxgl.GeoJSONSource | undefined;
    if (src) {
      src.setData(
        geometry
          ? { type: 'FeatureCollection', features: [{ type: 'Feature', geometry, properties: {} }] }
          : { type: 'FeatureCollection', features: [] },
      );
    }
    const center = geometry ? geometryCentroid(geometry) : null;
    if (center) positionCallout.current(center);
  });

  // Drop the highlight fill + geo-anchored callout. Needed when the shared
  // Add-my-place map leaves the building step (back to the static address step),
  // so no stale teal footprint/callout lingers on the now-display-only map.
  const clearAddressHighlight = useRef(() => {
    const map = mapRef.current;
    if (map) {
      const src = map.getSource('amp-building-highlight') as mapboxgl.GeoJSONSource | undefined;
      if (src) src.setData({ type: 'FeatureCollection', features: [] });
    }
    if (calloutMarkerRef.current) {
      try {
        calloutMarkerRef.current.remove();
      } catch {
        // ignore
      }
      calloutMarkerRef.current = null;
    }
    const root = calloutRootRef.current;
    if (root) {
      calloutRootRef.current = null;
      queueMicrotask(() => {
        try {
          root.unmount();
        } catch {
          // ignore
        }
      });
    }
  });

  // Resolve the building at the address coordinate and highlight it. `idle` is
  // unreliable on this map (it can fire before the building tiles render, or be
  // blocked by continuous repaint), so poll until the footprint is queryable;
  // if it never resolves, still anchor the callout to the coordinate.
  const initialResolveTimerRef = useRef<number | null>(null);
  // Monotonic id for building-click reverse-geocode requests. Rapid clicks can
  // resolve out of order, so each completion is only applied if it is still the
  // latest request (older completions are ignored to avoid overwriting newer
  // selections). A consumer's selectionRef replaces the private counter, so its
  // own closes and opens cancel pending clicks too.
  const ownGeocodeRequestIdRef = useRef(0);
  const reverseGeocodeRequestIdRef = selectionRef ?? ownGeocodeRequestIdRef;
  const resolveInitialHighlight = useRef(() => {
    const coord = addressHighlightAtRef.current;
    if (!coord) return;
    if (initialResolveTimerRef.current !== null) {
      window.clearTimeout(initialResolveTimerRef.current);
      initialResolveTimerRef.current = null;
    }
    let attempts = 0;
    const tryResolve = () => {
      initialResolveTimerRef.current = null;
      const map = mapRef.current;
      const target = addressHighlightAtRef.current;
      if (!map || !target) return;
      ensureHighlightLayers.current();
      const geometry = findBuildingFootprintAtCoord(map, target);
      if (geometry) {
        applyHighlightGeometry.current(geometry);
        return;
      }
      attempts += 1;
      if (attempts >= 12) {
        positionCallout.current([target.lng, target.lat]);
        return;
      }
      initialResolveTimerRef.current = window.setTimeout(tryResolve, 150);
    };
    tryResolve();
  });

  useEffect(() => {
    addressCalloutRef.current = addressCallout;
    if (calloutRootRef.current) {
      calloutRootRef.current.render(<>{addressCallout}</>);
    }
  }, [addressCallout]);

  useEffect(() => {
    addressHighlightAtRef.current = addressHighlightAt;
    if (!mapRef.current) return undefined;
    if (!addressHighlightAt) {
      clearAddressHighlight.current();
      return undefined;
    }
    resolveInitialHighlight.current();
    return () => {
      if (initialResolveTimerRef.current !== null) {
        window.clearTimeout(initialResolveTimerRef.current);
        initialResolveTimerRef.current = null;
      }
    };
  }, [addressHighlightAt]);

  useEffect(() => {
    onUserMapDragEndRef.current = onUserMapDragEnd;
  }, [onUserMapDragEnd]);

  useEffect(() => {
    onMapReadyRef.current = onMapReady;
  }, [onMapReady]);

  useEffect(() => {
    onUserMapInteractionRef.current = onUserMapInteraction;
  }, [onUserMapInteraction]);

  useEffect(() => {
    onMapMoveStartRef.current = onMapMoveStart;
  }, [onMapMoveStart]);

  useEffect(() => {
    onMapMoveDiscardedRef.current = onMapMoveDiscarded;
  }, [onMapMoveDiscarded]);

  useEffect(() => {
    onGeolocateStartRef.current = onGeolocateStart;
  }, [onGeolocateStart]);

  useEffect(() => {
    onGeolocateEndRef.current = onGeolocateEnd;
  }, [onGeolocateEnd]);

  useEffect(() => {
    onVisibleCountsChangeRef.current = onVisibleCountsChange;
  }, [onVisibleCountsChange]);

  useEffect(() => {
    offMarketFeatureCollectionRef.current = offMarketFeatureCollection;
  }, [offMarketFeatureCollection]);

  useEffect(() => {
    simplifiedMapRef.current = simplifiedMap;
  }, [simplifiedMap]);

  // Update the module-level icon locale + swap layer icon-image expressions
  // when the i18n language changes. clicked-buildings source data does NOT
  // need re-setting — only the layer's image lookup table needs to shift.
  // Also pre-warm canvas-generated icons (counts >=10) for the new locale.
  useEffect(() => {
    const locale: IconLocale = i18n.language.startsWith('fi') ? 'fi' : 'en';
    setIconLocale(locale);
    const m = mapRef.current;
    if (!m) return;
    if (m.getLayer('building-off-market-sell-icon')) {
      m.setLayoutProperty(
        'building-off-market-sell-icon',
        'icon-image',
        sellIconImageExpr(false, locale),
      );
    }
    if (m.getLayer('building-off-market-sell-icon-selected')) {
      m.setLayoutProperty(
        'building-off-market-sell-icon-selected',
        'icon-image',
        sellIconImageExpr(true, locale),
      );
    }
    const src = m.getSource('clicked-buildings') as
      | mapboxgl.GeoJSONSource
      | undefined;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const data = (src as any)?._data as GeoJSON.FeatureCollection | undefined;
    if (data && Array.isArray(data.features)) {
      void ensureRuntimeSellIcons(m, data);
    }
  }, [i18n.language]);

  useEffect(() => {
    onVisibleBoundsChangeRef.current = onVisibleBoundsChange;
  }, [onVisibleBoundsChange]);

  const [showClickJaakkoHint, setShowClickJaakkoHint] = useState<boolean>(
    () => {
      const storedValue = localStorage.getItem('showClickJaakkoHint');
      return storedValue === null ? true : storedValue === 'true';
    },
  );

  const [showJaakkoSpeechBubble, setShowJaakkoSpeechBubble] = useState(false);
  const [zoom, setZoom] = useState(15.5);

  // The camera the last acted-on moveend reported, so a container resize (the
  // mobile soft keyboard) can be told from a real map movement. Two snapshots,
  // not one: on a map with no parent-owned listings both this effect's handler
  // and the counts fetch below are registered on moveend, and `cameraMoved`
  // updates the snapshot it is given — sharing one would let whichever ran
  // first tell the other that nothing had moved.
  const lastEmittedCameraRef = useRef<{
    lng: number;
    lat: number;
    zoom: number;
    bearing: number;
    pitch: number;
  } | null>(null);
  // Set when a keyboard shrink was skipped, so the matching restore can be
  // skipped too. The restore cannot be recognised by focus: dismissing often
  // removes the field from the page rather than blurring it, and even when it
  // does blur, the viewport comes back a moment later. It is recognised by
  // shape instead — the view returning to the area we were already showing.
  const inKeyboardShrinkRef = useRef(false);

  // The area the last emitted bounds covered, so a resize that only shrinks the
  // view is told apart from one that reveals new map.
  const lastEmittedBoundsRef = useRef<{ w: number; s: number; e: number; n: number } | null>(null);
  const lastFetchedCameraRef = useRef<{
    lng: number;
    lat: number;
    zoom: number;
    bearing: number;
    pitch: number;
  } | null>(null);
  const animationProgressRef = useRef(0);
  let animationFrameId = useRef<number>();

  const [isIframeLoading, setIsIframeLoading] = useState(true);
  const [mapError, setMapError] = useState<string | null>(null);
  // Gate the static (hideLabels) map's first paint: keep the canvas hidden until
  // labels are hidden so the address step never flashes Mapbox labels. Maps that
  // don't hide labels (the landing map) are never gated.
  const [labelsHidden, setLabelsHidden] = useState(false);
  const smallMapRecoveryNonceRef = useRef(0);
  const geolocateHintTimeoutRef = useRef<number>();
  const [geolocateHintKey, setGeolocateHintKey] = useState<string | null>(null);

  const hideGeolocateHint = () => {
    if (geolocateHintTimeoutRef.current) {
      window.clearTimeout(geolocateHintTimeoutRef.current);
      geolocateHintTimeoutRef.current = undefined;
    }

    setGeolocateHintKey(null);
  };

  const showGeolocateHint = (hintKey: string) => {
    if (geolocateHintTimeoutRef.current) {
      window.clearTimeout(geolocateHintTimeoutRef.current);
    }

    setGeolocateHintKey(hintKey);
    geolocateHintTimeoutRef.current = window.setTimeout(() => {
      geolocateHintTimeoutRef.current = undefined;
      setGeolocateHintKey(null);
    }, GEOLOCATE_HINT_HIDE_DELAY_MS);
  };

  useEffect(() => {
    return () => {
      if (geolocateHintTimeoutRef.current) {
        window.clearTimeout(geolocateHintTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    const setVh = () => {
      const vh = window.innerHeight * 0.01;
      document.documentElement.style.setProperty('--vh', `${vh}px`);
    };

    setVh();

    window.addEventListener('resize', setVh);

    return () => {
      window.removeEventListener('resize', setVh);
    };
  }, []);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        speechBubbleRef.current &&
        !speechBubbleRef.current.contains(event.target as Node) &&
        jaakkoInfoRef.current &&
        !jaakkoInfoRef.current.contains(event.target as Node)
      ) {
        setShowJaakkoSpeechBubble(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [speechBubbleRef, jaakkoInfoRef]);

  useEffect(() => {
    try {
      mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_ACCESS_TOKEN;

      const initialMapCenter = lastMapBoundaryBoxCenter || mapCenter;
      styleUrlRef.current = `mapbox://styles/jaakkocom/${process.env.REACT_APP_MAPBOX_STYLE_ID}`;

      if (!mapboxgl.supported()) {
        setMapError('WebGL is not supported by this browser. Please use a modern browser with WebGL support.');
        return;
      }

      mapRef.current = new mapboxgl.Map({
        container: mapContainerRef.current!,
        style: styleUrlRef.current,
        center: [initialMapCenter.lng, initialMapCenter.lat],
        zoom: lastZoomLevel || initialMapZoom,
        interactive: !staticMap,
        failIfMajorPerformanceCaveat: false,
      });
      // Captured once: the sell layers are configured from this at creation
      // and the flag never changes for a live consumer.
      setSellMarkerDecluttering(mapRef.current, !!declutterSellMarkers);
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (window as any).__map = mapRef.current;
    } catch (error) {
      console.error('Failed to initialize map:', error);
      setMapError('Failed to initialize the map. This may be due to limited graphics capabilities. Please try refreshing or using a different browser.');
      return;
    }

    // Hide labels as early as the style is parsed (before glyphs paint) and only
    // then reveal the canvas, so the static address map never flashes labels.
    // Runs before 'load' (which waits for tiles too); self-removes after the
    // first style-loaded pass — ongoing re-applies are handled on 'styledata'.
    const earlyHideLabels = () => {
      const map = mapRef.current;
      if (!map || !map.isStyleLoaded()) return;
      if (hideLabelsRef.current) hideTextLabelLayers(map, hiddenLabelLayerIdsRef.current);
      setLabelsHidden(true);
      map.off('styledata', earlyHideLabels);
    };
    mapRef.current.on('styledata', earlyHideLabels);

    mapRef.current.on('load', () => {
      try {
        console.log('Map loaded');
        if (mapRef.current) onMapReadyRef.current?.(mapRef.current);

        const hideLabelsIfNeeded = () => {
          const map = mapRef.current;
          if (!map || !hideLabelsRef.current) return;
          hideTextLabelLayers(map, hiddenLabelLayerIdsRef.current);
        };

        const restoreStyleLayers = () => {
          const map = mapRef.current;
          if (!map || !map.isStyleLoaded()) return;
          addHoverLayer(mapRef, 'building', 'building-hover-outline');
          // Re-add the address-highlight overlay too (style reloads drop it).
          ensureHighlightLayers.current();
          // Re-apply the off-market listings source + icon layers — a style
          // reload (recovery setStyle / WebGL context loss) drops them, and
          // hover/highlight alone would leave the buy/sell icons missing.
          restoreOffMarketLayers.current();
          hideLabelsIfNeeded();
        };

        const scheduleStyleLayerRestore = () => {
          if (styleRestoreTimerRef.current !== null) return;
          styleRestoreTimerRef.current = window.setTimeout(() => {
            styleRestoreTimerRef.current = null;
            restoreStyleLayers();
          }, 0);
        };

        restoreStyleLayers();
        // Safety net: guarantee the canvas is revealed even if the early
        // `styledata` hide path was missed (idempotent with it).
        setLabelsHidden(true);

        mapRef.current!.on('styledata', scheduleStyleLayerRestore);

        // JAA-326: Samsung Xclipse GPUs corrupt Mapbox symbol rendering in
        // Chrome — swap marker visuals to DOM markers there (symbol layers
        // stay for queryRenderedFeatures click/hover, hidden via opacity).
        // Isolated from the surrounding load handler: this is a cosmetic
        // workaround, so a failure here must not reach the outer catch, which
        // would abort the rest of the map setup and surface a fatal map error
        // to exactly the devices the fallback is meant to help.
        // JAA-450: full Chrome version hints are async. Keep the rest of map
        // setup running, and never attach to a map replaced while they resolve.
        const fallbackMap = mapRef.current!;
        void domMarkerFallbackEnabled().then((enabled) => {
          if (enabled && mapRef.current === fallbackMap) {
            attachDomMarkerFallback(fallbackMap, () => currentIconLocale);
          }
        }).catch((err) => {
          console.error('DOM marker fallback failed; keeping symbol layers:', err);
        });

        // "Add my place" building step: resolve + highlight the building at the
        // address coordinate and anchor the callout to it (polls until the
        // building tiles are queryable).
        if (addressHighlightAtRef.current) {
          resolveInitialHighlight.current();
        }

        const refreshBuildingDots = () => {
          const m = mapRef.current;
          if (!m || !m.isStyleLoaded()) return;
          // Desktop has hover-state to indicate clickability; dots are mobile/
          // tablet only. If desktop, drain the source so any existing dots clear.
          if (isDesktop()) {
            const existingSrc = m.getSource('building-dots') as
              | mapboxgl.GeoJSONSource
              | undefined;
            if (existingSrc) {
              existingSrc.setData({ type: 'FeatureCollection', features: [] });
            }
            return;
          }
          let features: mapboxgl.MapboxGeoJSONFeature[];
          try {
            features = m.querySourceFeatures('composite', { sourceLayer: 'building' });
          } catch (e) {
            return;
          }
          const listingIds = new Set<string>();
          const src = m.getSource('clicked-buildings');
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const cb = (src && (src as any)._data) as
            | GeoJSON.FeatureCollection
            | undefined;
          (cb?.features || []).forEach((f) => {
            const id = String(f.properties?.building_id ?? f.id ?? '');
            if (id) listingIds.add(id);
          });
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const vertexCount = (geom: any): number => {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const walk = (c: any): number => {
              if (!Array.isArray(c)) return 0;
              if (typeof c[0] === 'number') return 1;
              return c.reduce((n: number, x: unknown) => n + walk(x), 0);
            };
            return walk(geom?.coordinates);
          };
          const best = new Map<string, { geom: GeoJSON.Geometry; v: number }>();
          for (const f of features) {
            const id = String(f.id ?? '');
            if (!id || listingIds.has(id)) continue;
            const t = f.properties?.type;
            if (t === 'building:part' || t === 'roof') continue;
            const v = vertexCount(f.geometry);
            const prev = best.get(id);
            if (!prev || v > prev.v) {
              best.set(id, { geom: f.geometry as GeoJSON.Geometry, v });
            }
          }
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const interiorPoint = (geom: any): [number, number] | null => {
            // Pole-of-inaccessibility — always inside the polygon, unlike
            // vertex-average centroid which falls outside for L/U shapes.
            const coords = geom?.coordinates;
            if (!coords) return null;
            const rings =
              geom.type === 'Polygon'
                ? coords
                : geom.type === 'MultiPolygon'
                  ? coords[0]
                  : null;
            if (!Array.isArray(rings) || rings.length === 0) return null;
            try {
              const p = polylabel(rings, 1e-5);
              if (Array.isArray(p) && p.length >= 2) return [p[0], p[1]];
            } catch {
              // ignore
            }
            return null;
          };
          const polys: GeoJSON.Feature[] = [];
          Array.from(best.entries()).forEach(([id, b]) => {
            // Pin polylabel per building_id — without cache, tile-split halves
            // per zoom yield different polylabels, causing visible icon drift.
            let c = polylabelCache.get(id) ?? null;
            if (!c) {
              c = interiorPoint(b.geom);
              if (c) polylabelCache.set(id, c);
            }
            if (!c) return;
            polys.push({
              type: 'Feature',
              id: buildingIdAsMapboxId(id),
              geometry: { type: 'Point', coordinates: c },
              properties: { building_id: id },
            });
          });
          const data: GeoJSON.FeatureCollection = {
            type: 'FeatureCollection',
            features: polys,
          };
          const existing = m.getSource('building-dots') as
            | mapboxgl.GeoJSONSource
            | undefined;
          if (existing) {
            existing.setData(data);
          } else {
            m.addSource('building-dots', { type: 'geojson', data });
          }
          if (!m.getLayer('building-clickable-dots')) {
            try {
              m.addLayer({
                id: 'building-clickable-dots',
                type: 'symbol',
                source: 'building-dots',
                minzoom: BUILDING_DOTS_MIN_ZOOM,
                layout: {
                  'icon-image': 'building-dot',
                  'icon-size': 1,
                  'icon-anchor': 'center',
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                },
                paint: {
                  'icon-opacity': iconOpacityHover(false),
                },
              });
            } catch (e) {
              console.error('Failed to add building-clickable-dots layer:', e);
            }
          }
          // Paired -selected layer — Mapbox forbids feature-state in layout
          // properties, so hover icon-swap is done via two layers + opacity.
          if (!m.getLayer('building-clickable-dots-selected')) {
            try {
              m.addLayer({
                id: 'building-clickable-dots-selected',
                type: 'symbol',
                source: 'building-dots',
                minzoom: BUILDING_DOTS_MIN_ZOOM,
                layout: {
                  'icon-image': 'building-dot-selected',
                  'icon-size': 1,
                  'icon-anchor': 'center',
                  'icon-allow-overlap': true,
                  'icon-ignore-placement': true,
                },
                paint: {
                  'icon-opacity': iconOpacityHover(true),
                },
              });
            } catch (e) {
              console.error('Failed to add building-clickable-dots-selected layer:', e);
            }
          }
        };
        // Skip dot wiring entirely on simplified (Add-my-place) maps so the
        // `building-clickable-dots*` layers and `building-dots` source are never
        // created.
        if (!simplifiedMap) {
          mapRef.current!.on('moveend', refreshBuildingDots);
          // Re-evaluate dot visibility when the viewport crosses the desktop
          // breakpoint (e.g. user resizes browser between mobile and desktop).
          window.addEventListener('resize', refreshBuildingDots);
          refreshBuildingDots();
        }

        const HEART_MIN_ZOOM = 14;
        // ~100 years; backend treats <=0 as the 7-day default, so all-time is a
        // large positive withinDays rather than 0.
        const ALL_TIME_WITHIN_DAYS = 36500;
        // The last week is what the burst is about, but the country sees well
        // under a like a day, so most viewports would hold one or none. When the
        // week is that thin the burst reaches further back until it has this many
        // hearts, or until the area runs out (JAA-377).
        const HEART_RECENT_WITHIN_DAYS = 7;
        // Kept low on purpose: at 15 the animated hearts read as a distraction.
        const HEART_TARGET_LIKES = 3;
        // Animation total = 1.4s (matches CSS keyframes); each heart auto-removes
        // after one full cycle. The fireworks cadence (random 50–100ms stagger,
        // random order, size jitter) lives in heartAnimation.buildFireworksSchedule.
        const HEART_ANIMATION_MS = 2000;

        const teardownHearts = () => {
          const sched = heartScheduleRef.current;
          if (sched.nextTimer !== null) {
            window.clearTimeout(sched.nextTimer);
            sched.nextTimer = null;
          }
          if (heartIdleTimerRef.current !== null) {
            window.clearTimeout(heartIdleTimerRef.current);
            heartIdleTimerRef.current = null;
          }
          sched.deathTimers.forEach((t) => window.clearTimeout(t));
          sched.deathTimers.clear();
          heartMarkersRef.current.forEach((entry) => {
            entry.marker.remove();
            queueMicrotask(() => entry.root.unmount());
          });
          heartMarkersRef.current.clear();
        };

        // Pixel-space jitter so a building's multiple likes fan into a small
        // cluster instead of stacking on one point. project→offset→unproject
        // keeps the spread visually constant across zoom levels.
        const jittered = (
          map: mapboxgl.Map,
          base: [number, number],
        ): [number, number] => {
          const p = map.project(base);
          const R = 14; // px radius
          const ll = map.unproject([
            p.x + (Math.random() - 0.5) * 2 * R,
            p.y + (Math.random() - 0.5) * 2 * R,
          ]);
          return [ll.lng, ll.lat];
        };

        const spawnHeart = (
          map: mapboxgl.Map,
          sched: typeof heartScheduleRef.current,
          coords: [number, number],
          markerKey: string,
          scale: number,
        ) => {
          const el = document.createElement('div');
          const root = ReactDOM.createRoot(el);
          root.render(<AnimatedHeartMarker scale={scale} />);
          const marker = new mapboxgl.Marker({
            element: el,
            anchor: 'bottom',
            offset: [0, 0],
          })
            .setLngLat(coords)
            .addTo(map);
          heartMarkersRef.current.set(markerKey, { marker, root });
          const deathTimer = window.setTimeout(() => {
            sched.deathTimers.delete(deathTimer);
            const entry = heartMarkersRef.current.get(markerKey);
            if (!entry) return;
            entry.marker.remove();
            queueMicrotask(() => entry.root.unmount());
            heartMarkersRef.current.delete(markerKey);
          }, HEART_ANIMATION_MS);
          sched.deathTimers.add(deathTimer);
        };

        // Schedule each heart at its staggered delay. Does NOT tear down
        // existing hearts — callers decide whether to clear first.
        const runBurst = (spawns: HeartSpawn[]) => {
          const sched = heartScheduleRef.current;
          for (const s of spawns) {
            const t = window.setTimeout(() => {
              sched.deathTimers.delete(t);
              const m2 = mapRef.current;
              if (!m2 || m2.getZoom() < HEART_MIN_ZOOM) return;
              const coords =
                s.indexInCluster === 0 ? s.base : jittered(m2, s.base);
              spawnHeart(
                m2,
                sched,
                coords,
                `${s.buildingId}:${Date.now()}:${s.indexInCluster}:${t}`,
                s.scale,
              );
            }, s.startDelayMs);
            sched.deathTimers.add(t);
          }
        };

        playLikeAnimationRef.current = (building) => {
          const m = mapRef.current;
          if (!m || m.getZoom() < HEART_MIN_ZOOM) return;
          const coords = getBuildingLikeCoordinates(building);
          if (!coords) return;

          runBurst(
            buildFireworksSchedule([
              {
                type: 'Feature',
                id: building.id,
                properties: {
                  building_id: building.id,
                  like_count: 1,
                },
                geometry: {
                  type: 'Point',
                  coordinates: [coords.lng, coords.lat],
                },
              },
            ]),
          );
        };

        // Recent-like building features currently inside the viewport.
        // Defaults to the 7-day recent-likes set (idle trickle), but accepts an
        // explicit collection so the all-time fallback can reuse the bounds filter.
        const inViewFeatures = (
          fc: GeoJSON.FeatureCollection | null = recentLikesRef.current,
        ): GeoJSON.Feature[] => {
          const m = mapRef.current;
          if (!m || !fc || !Array.isArray(fc.features)) return [];
          const b = m.getBounds();
          if (!b) return [];
          const out: GeoJSON.Feature[] = [];
          for (const f of fc.features) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const g = f.geometry as any;
            if (g?.type !== 'Point' || !Array.isArray(g.coordinates)) continue;
            const [lng, lat] = g.coordinates as [number, number];
            if (b.contains([lng, lat])) out.push(f);
          }
          return out;
        };

        // Idle trickle: 1–2 random recent likes, no teardown, re-armed on a
        // flexible 30–90s cadence (first arm ~60s after a burst).
        const HEART_IDLE_FIRST_MS = 60000;
        const scheduleIdleSprinkle = (initial: boolean) => {
          if (heartIdleTimerRef.current !== null) {
            window.clearTimeout(heartIdleTimerRef.current);
          }
          const delay = initial
            ? HEART_IDLE_FIRST_MS
            : 30000 + Math.random() * 60000; // 30–90s
          heartIdleTimerRef.current = window.setTimeout(idleSprinkle, delay);
        };
        const idleSprinkle = () => {
          heartIdleTimerRef.current = null;
          const m = mapRef.current;
          if (m && m.getZoom() >= HEART_MIN_ZOOM) {
            const features = inViewFeatures();
            if (features.length > 0) {
              const count = 1 + Math.floor(Math.random() * 2); // 1 or 2
              runBurst(pickSprinkle(features, { count }));
            }
          }
          scheduleIdleSprinkle(false);
        };

        // Fire the fireworks burst for the current viewport's recent likes.
        // Tears down any in-flight hearts first (fresh area), then arms idle.
        const syncHeartMarkers = () => {
          const m = mapRef.current;
          if (!m) return;
          teardownHearts();
          if (m.getZoom() < HEART_MIN_ZOOM) return;
          const features = inViewFeatures();
          if (features.length === 0) return;
          runBurst(buildFireworksSchedule(features));
          scheduleIdleSprinkle(true);
        };

        // Threshold gate: only refetch + refire when the viewport moved enough
        // to feel like a new area (half-viewport pan or a full zoom level).
        // Small nudges leave the current hearts playing.
        const maybeRefire = () => {
          const m = mapRef.current;
          if (!m) return;
          const last = lastFiredViewRef.current;
          if (last) {
            const prev = m.project(last.center);
            const next = m.project(m.getCenter());
            if (
              !shouldRefire({
                prevCenterPx: { x: prev.x, y: prev.y },
                nextCenterPx: { x: next.x, y: next.y },
                prevZoom: last.zoom,
                nextZoom: m.getZoom(),
                viewportWidthPx: m.getContainer().clientWidth,
              })
            ) {
              return;
            }
          }
          fetchRecentLikes();
        };

        // One-shot fallback: when a viewport has no likes in the last 7 days,
        // show the single most-recent like of all time so the area never feels
        // dead. Plays a single heart once — deliberately NOT added to
        // recentLikesRef and never arms the idle trickle, so a stale like never
        // re-pops like fresh activity.
        //
        // Dedupe: `fetchRecentLikes` can run more than once for what is really
        // the same area (an extra moveend/zoomend, a programmatic recenter, the
        // initial load coinciding with a pan, or overlapping async calls).
        // Re-rendering the lone fallback heart then reads as it popping "twice
        // in a row". So if the same building was just shown within the heart's
        // animation window, leave it playing rather than tearing it down and
        // replaying it.

        // Widen the window to all time and keep only enough of it to reach the
        // target. Returns the recent collection unchanged when the wider request
        // fails, so a thin area still shows the little it has (JAA-377).
        const fetchTopUpLikes = async (
          recent: GeoJSON.FeatureCollection,
        ): Promise<GeoJSON.FeatureCollection> => {
          const m = mapRef.current;
          if (!m) return recent;
          const b = m.getBounds();
          if (!b) return recent;
          const sw = b.getSouthWest();
          const ne = b.getNorthEast();
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_URL}/likes-in-bounds`,
              {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  bounds: { sw: [sw.lng, sw.lat], ne: [ne.lng, ne.lat] },
                  orderBy: 'timestamp',
                  order: 'desc',
                  limit: HEART_TARGET_LIKES,
                  withinDays: ALL_TIME_WITHIN_DAYS,
                }),
              },
            );
            if (!res.ok) return recent;
            const wider = (await res.json()) as GeoJSON.FeatureCollection;
            return {
              ...wider,
              features: trimToLikeBudget(
                inViewFeatures(wider),
                HEART_TARGET_LIKES,
              ) as GeoJSON.Feature[],
            };
          } catch {
            return recent;
          }
        };

        const fetchRecentLikes = async () => {
          const m = mapRef.current;
          if (!m) return;
          const b = m.getBounds();
          if (!b) return;
          // Mark this as the last view we fetched for — even if it turns out to
          // have no likes — so the threshold blocks small follow-up moves
          // instead of refetching on every nudge in empty areas.
          lastFiredViewRef.current = { center: m.getCenter(), zoom: m.getZoom() };
          const sw = b.getSouthWest();
          const ne = b.getNorthEast();
          try {
            const res = await fetch(
              `${process.env.REACT_APP_API_URL}/likes-in-bounds`,
              {
                method: 'POST',
                credentials: 'include',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                  bounds: {
                    sw: [sw.lng, sw.lat],
                    ne: [ne.lng, ne.lat],
                  },
                  orderBy: 'timestamp',
                  order: 'desc',
                  // Area-based hearts: all distinct-building likes from the
                  // recent window in view, uncapped. A thin answer is topped up
                  // from further back below.
                  withinDays: HEART_RECENT_WITHIN_DAYS,
                }),
              },
            );
            if (!res.ok) return;
            const data = (await res.json()) as GeoJSON.FeatureCollection;
            const recentInView = inViewFeatures(data);
            const recentLikes = recentInView.reduce(
              (n, f) => n + featureLikeCount(f),
              0,
            );
            // Asking for HEART_TARGET_LIKES buildings is the cheap way to be sure
            // of that many likes: the route caps on distinct buildings and every
            // building is worth at least one heart, so the answer can only
            // overshoot, and trimToLikeBudget cuts it back to the target.
            recentLikesRef.current =
              recentLikes >= HEART_TARGET_LIKES
                ? data
                : await fetchTopUpLikes(data);
            syncHeartMarkers();
          } catch {
            // hearts are a secondary signal — silent failure is acceptable
          }
        };
        // Debounce on map idle so a pan gesture coalesces into one check. The
        // threshold gate (maybeRefire) then decides whether this counts as a new
        // area worth refetching + replaying.
        let recentLikesTimer: number | null = null;
        const scheduleMaybeRefire = () => {
          if (recentLikesTimer !== null) {
            window.clearTimeout(recentLikesTimer);
          }
          recentLikesTimer = window.setTimeout(maybeRefire, 400);
        };
        // `once('idle')` is unreliable here — idle can fire before this
        // handler registers when tiles are cached. First load fires once.
        // Skipped on simplified (Add-my-place) maps so `/likes-in-bounds` is
        // never fetched and no heart markers spawn.
        if (!simplifiedMap) {
          window.setTimeout(fetchRecentLikes, 800);
          mapRef.current!.on('moveend', scheduleMaybeRefire);
        }

        const refreshListingGeometries = () => {
          const m = mapRef.current;
          if (!m) return;
          const src = m.getSource('clicked-buildings') as
            | mapboxgl.GeoJSONSource
            | undefined;
          if (!src) return;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const d = (src as any)._data as GeoJSON.FeatureCollection | undefined;
          if (!d || !Array.isArray(d.features)) return;
          src.setData(rewriteListingGeometriesToPolylabel(m, d));
        };
        mapRef.current!.on('moveend', refreshListingGeometries);
        if (!simplifiedMap) {
          mapRef.current!.on('zoomend', scheduleMaybeRefire);
        }
        // Do NOT listen on `sourcedata` for refreshBuildingDots → setData loop
        // (`setData('building-dots', ...)` re-fires `sourcedata`). But it IS
        // safe to use sourcedata for a side-effect that does NOT call setData.
        // GeoJSONSource.setData() wipes per-source feature-state — without this
        // re-apply, every moveend (which triggers setData on clicked-buildings)
        // resets a live hover swap back to the default pill.
        mapRef.current!.on('sourcedata', (ev) => {
          if (ev.sourceId !== 'clicked-buildings') return;
          if (ev.dataType !== 'source') return;
          if (!ev.isSourceLoaded) return;
          const map = mapRef.current;
          if (!map) return;
          // Re-apply the live hover AND the pinned (preview) highlight — setData
          // wiped per-source feature-state. sourcedata fires many times per
          // tick; skip when already current so we don't burn triggerRepaint.
          const ids: string[] = [];
          if (hoverStateRef.current.buildingId) ids.push(hoverStateRef.current.buildingId);
          if (pinnedBuildingRef.current && pinnedBuildingRef.current !== hoverStateRef.current.buildingId) {
            ids.push(pinnedBuildingRef.current);
          }
          ids.forEach((bid) => {
            const mid = buildingIdAsMapboxId(bid);
            const cur = map.getFeatureState({ source: 'clicked-buildings', id: mid });
            if (cur && cur.hover === true) return;
            setHoverByBuildingId(map, bid, null);
          });
        });


        const setHover = (nextBid: string | null): void => {
          const map = mapRef.current;
          if (!map) return;
          const prevBid = hoverStateRef.current.buildingId;
          if (prevBid === nextBid) return;
          // Never un-hover the pinned (preview) building when the mouse leaves it.
          const prevToClear = prevBid === pinnedBuildingRef.current ? null : prevBid;
          setHoverByBuildingId(map, nextBid, prevToClear);
          hoverStateRef.current = { buildingId: nextBid };
        };

        /** Shows the area-interest coin tooltip with the stage's copy (JAA-329 #16). */
        const showAreaCoinTooltip = (coin: { weight: number; lngLat: [number, number] }) => {
          const m = mapRef.current;
          if (!m) return;
          if (!areaTooltipRef.current) {
            areaTooltipRef.current = new mapboxgl.Popup({
              closeButton: false,
              closeOnClick: false,
              offset: 20,
              maxWidth: '250px',
              className: 'area-interest-tooltip',
            });
          }
          areaTooltipRef.current
            .setLngLat(coin.lngLat)
            .setText(t(`newLandingPage.explore.areaInterest.stage${stageForWeight(coin.weight)}`))
            .addTo(m);
        };
        /** Removes the coin tooltip if shown. */
        const hideAreaCoinTooltip = () => {
          areaTooltipRef.current?.remove();
        };
        // Map events stop at the canvas edge, so a tooltip would linger when
        // the pointer moves onto app UI or the user pans/zooms away — dismiss
        // on both. movestart also covers zooming past the coins' zoom band.
        mapRef.current!.on('movestart', hideAreaCoinTooltip);
        mapRef.current!.on('mouseout', hideAreaCoinTooltip);

        // Single mousemove handler — per-layer mouseenter won't fire when
        // moving building→building within the same layer.
        mapRef.current!.on('mousemove', (e: MapMouseEvent) => {
          if (window.innerWidth < 960) return;
          const map = mapRef.current;
          if (!map) return;

          // Desktop hover on an area-interest coin: tooltip follows the coin,
          // while the building hover/highlight below keeps working unchanged.
          const hoverCoin = demandCoinAt(map, e.point);
          if (hoverCoin) showAreaCoinTooltip(hoverCoin);
          else hideAreaCoinTooltip();

          const idLayers = [
            'building-off-market-sell-icon',
            'building-off-market-sell-dot',
            'building-off-market-buy-icon',
            'building-clickable-dots',
          ];
          const allLayers = ['building', ...idLayers].filter((id) => map.getLayer(id));
          const hits = allLayers.length
            ? map.queryRenderedFeatures(e.point, { layers: allLayers })
            : [];

          let polygonHit: mapboxgl.MapboxGeoJSONFeature | null = null;
          let idHit: mapboxgl.MapboxGeoJSONFeature | null = null;
          for (const f of hits) {
            const id = f.layer?.id;
            if (id === 'building' && polygonHit === null) polygonHit = f;
            else if (id && idLayers.includes(id) && idHit === null) idHit = f;
            if (polygonHit && idHit) break;
          }

          // Resolve backend building_id from whichever source can supply it.
          // Composite `building` feature.id == backend building_id (OSM way id),
          // so polygon-area hover (anywhere on the building polygon) still
          // syncs sell pill + dot. Icon hover falls through props.building_id.
          let nextBid: string | null = null;
          if (idHit) {
            const raw =
              (idHit.properties as { building_id?: string | number } | null)
                ?.building_id ?? idHit.id;
            if (raw !== undefined && raw !== null) nextBid = String(raw);
          } else if (polygonHit && polygonHit.id !== undefined && polygonHit.id !== null) {
            nextBid = String(polygonHit.id);
          }

          // Display-only maps (Add-my-place address step) suppress the building
          // hover highlight; the building step keeps it as a selection affordance.
          setHover(disableBuildingClickRef.current ? null : nextBid);
          map.getCanvas().style.cursor = (!disableBuildingClickRef.current && nextBid !== null) ? 'pointer' : '';
        });

        mapRef.current!.on('mouseout', () => setHover(null));

        /**
         * Map click: opens, or reports, the building under the pointer, and drops any
         * lookup that a newer event has made stale. One counter, `reverseGeocodeRequestIdRef`
         * (the consumer's `selectionRef` when given), decides what is stale. Cases:
         *
         * Disabled map (`disableBuildingClick`): nothing happens and nothing is superseded.
         *
         * Plain map (no `onBuildingPickStart`, no `addressHighlightAt`): every click takes a
         * fresh `clickId`, so it supersedes the lookup of the click before it, and so does
         * an unmount or a consumer close, because they advance the same counter.
         *  - Coin tap: shows the tooltip, selects no building, still supersedes.
         *  - Listing marker with `openPreviewOnClick`: `selectBuilding` at once from the
         *    marker data, then again after the geocode, only if `clickId` is still current.
         *  - Plain building or dot: `selectBuilding` once its name resolves, only if `clickId`
         *    is still current. The name is the geocoder's first address or, when it has none,
         *    the district alone; it never opens only when there is no district and no
         *    municipality name.
         *  - Listing marker without `openPreviewOnClick` (full-view maps): `selectBuilding`
         *    after the geocode with the first address. With none it never opens.
         *  - Click on no building: nothing is selected; the earlier lookup is still dropped.
         *
         * Pick map (Add my place building step): the click does NOT take a `clickId`; each
         * pick bumps the counter itself, so only the next pick supersedes it. A pick that was
         * announced by `onBuildingPickStart` always ends in exactly one of:
         *  - address found: `selectBuilding` with the address.
         *  - no address, lookup rejected, or `selectBuilding` throws: `onBuildingPickFailed`.
         *  - superseded by a newer pick or the map unmounting: neither, the newer pick reports.
         *  - click on a coin or on no building: no new pick, the pick in flight carries on.
         */
        mapRef.current!.on('click', (e: MapMouseEvent) => {
          if (disableBuildingClickRef.current) return;
          const isBuildingPick = Boolean(
            onBuildingPickStartRef.current || addressHighlightAtRef.current,
          );
          // A pick map is superseded only by its next pick, so a click on no building cannot strand it.
          const clickId = isBuildingPick
            ? reverseGeocodeRequestIdRef.current
            : (reverseGeocodeRequestIdRef.current += 1);
          // A tap/click on an area-interest coin shows its tooltip INSTEAD of
          // resolving a building under it (JAA-329 #16); any other click
          // dismisses the tooltip and falls through as before.
          const tappedCoin = mapRef.current ? demandCoinAt(mapRef.current, e.point) : null;
          if (tappedCoin) {
            showAreaCoinTooltip(tappedCoin);
            return;
          }
          hideAreaCoinTooltip();
          try {
            const m = mapRef.current;
            if (m?.getLayer('building-off-market-sell-icon')) {
              const hit = m.queryRenderedFeatures(e.point, {
                layers: ['building-off-market-sell-icon'],
              });
              for (const f of hit) {
                if (
                  f.properties?.is_new === true &&
                  f.properties?.is_seen !== true
                ) {
                  // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  const bid = String((f.properties as any)?.building_id ?? f.id ?? '');
                  if (bid) {
                    markBuildingSeen(bid);
                    const src = m.getSource('clicked-buildings') as
                      | mapboxgl.GeoJSONSource
                      | undefined;
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    const d = (src as any)?._data as
                      | GeoJSON.FeatureCollection
                      | undefined;
                    if (src && d) {
                      src.setData(rewriteListingGeometriesToPolylabel(m, d));
                    }
                  }
                  break;
                }
              }
            }
          } catch {
            // ignore — fall through to building page navigation
          }
          // Surface the click point so the building preview can anchor to it.
          const clickPoint = { x: e.point.x, y: e.point.y };
          handleOnClickBuildingShowBuildingPage(
            e,
            mapRef,
            (b) => {
              if (clickId === reverseGeocodeRequestIdRef.current) {
                selectBuildingRef.current(b, clickPoint, clickId);
              }
            },
            isBuildingPick
              ? (geometry, atLngLat, buildingId) => {
                // Building step: move the highlight + callout to the clicked
                // building and resolve its address from where the user clicked
                // (so different sides of a building give the right street),
                // nudged inward toward the footprint center to avoid bleeding to
                // a neighbour's address at the edges, then checked against the
                // footprint the way the building card does (JAA-410).
                if (geometry) applyHighlightGeometry.current(geometry);
                else positionCallout.current([atLngLat.lng, atLngLat.lat]);
                const center = geometry ? geometryCentroid(geometry) : null;
                const geocodeAt = center
                  ? {
                    lng: atLngLat.lng + (center[0] - atLngLat.lng) * CENTROID_NUDGE,
                    lat: atLngLat.lat + (center[1] - atLngLat.lat) * CENTROID_NUDGE,
                  }
                  : atLngLat;
                // The pick bumps the shared counter itself, which retires any earlier pick.
                const requestId = (reverseGeocodeRequestIdRef.current += 1);
                onBuildingPickStartRef.current?.();
                Promise.all([
                  reverseGeocodeNearestAddress(geocodeAt),
                  fetchSideAddressesWithDeadline(
                    geocodeAt,
                    geometry,
                    process.env.REACT_APP_MAPBOX_ACCESS_TOKEN || '',
                  ),
                ]).then(([nearest, sides]) => {
                  const addr = resolvePrimaryAddress(nearest, sides, geocodeAt);
                  if (
                    requestId !== reverseGeocodeRequestIdRef.current ||
                    (!onBuildingPickStartRef.current && !addressHighlightAtRef.current)
                  ) {
                    return;
                  }
                  // No address for the clicked building: end the pick instead of leaving it pending (JAA-410).
                  if (!addr) {
                    onBuildingPickFailedRef.current?.();
                    return;
                  }
                  selectBuildingRef.current({
                    id: buildingId,
                    centroid: { lat: geocodeAt.lat, lng: geocodeAt.lng },
                    addresses: [addr],
                    properties: { name: '', addresses: [addr] },
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    geometry: (geometry ?? { type: 'Polygon', coordinates: [] }) as any,
                    pois: [],
                  });
                }).catch(() => {
                  if (requestId === reverseGeocodeRequestIdRef.current) {
                    onBuildingPickFailedRef.current?.();
                  }
                });
              }
              : undefined,
            openPreviewOnClick,
          );
        });

        const checkAndStartAnimation = () => {
          if (
            mapRef.current?.getLayer('building-jaakko-outline') &&
            !animationFrameId.current
          ) {
            animateGlow();
          }
        };

        mapRef.current!.on('sourcedata', checkAndStartAnimation);
        mapRef.current!.on('styledata', checkAndStartAnimation);

        if (!isLandingPage) {
          mapRef.current!.addControl(new mapboxgl.NavigationControl(), 'top-right');
          document
            .querySelector('.mapboxgl-ctrl-top-right')
            ?.classList.add('custom-navigation-control');
        }

        const geolocateControl = new mapboxgl.GeolocateControl({
          positionOptions: { enableHighAccuracy: true },
          fitBoundsOptions: { maxZoom: GEOLOCATE_TARGET_ZOOM },
          trackUserLocation: false,
          showAccuracyCircle: false,
          showUserHeading: false,
        });

        geolocateControl.on('geolocate', (position: GeolocationPosition) => {
          const shouldForceRecenter = explicitGeolocateRequestRef.current;
          if (!mapRef.current) {
            finishExplicitGeolocateRequest();
            return;
          }

          const { latitude, longitude, accuracy } = position.coords;

          try {
            console.info('Geolocate control received browser position', {
              latitude,
              longitude,
              accuracy,
              isIosChrome: isIosChrome(),
              userAgent: navigator.userAgent,
            });

            // iPhone Chrome sometimes returns very coarse locations; Mapbox
            // would fit the camera to the accuracy radius and zoom out too far.
            // Explicit custom-button clicks should always win over any map
            // movement that happened while the browser was resolving location.
            if (
              shouldForceRecenter ||
              mapRef.current.getZoom() < GEOLOCATE_TARGET_ZOOM ||
              accuracy > APPROXIMATE_LOCATION_THRESHOLD_METERS
            ) {
              mapRef.current.easeTo({
                center: [longitude, latitude],
                zoom: GEOLOCATE_TARGET_ZOOM,
                duration: 600,
                essential: true,
              });
            }

            if (accuracy > APPROXIMATE_LOCATION_THRESHOLD_METERS) {
              showGeolocateHint(
                isIosChrome()
                  ? 'propertyMap.approximateLocationHintIosChrome'
                  : 'propertyMap.approximateLocationHint',
              );
            } else {
              hideGeolocateHint();
            }
          } finally {
            finishExplicitGeolocateRequest();
          }
        });

        geolocateControl.on('error', (error: GeolocationPositionError) => {
          console.warn('Geolocate control error', error);
          hideGeolocateHint();
          finishExplicitGeolocateRequest();
        });

        mapRef.current!.addControl(geolocateControl, 'bottom-right');
        geolocateControlRef.current = geolocateControl;

        if (onVisibleBoundsChangeRef.current && mapRef.current) {
          const initialBounds = mapRef.current.getBounds();
          // Record the camera and the area these bounds belong to. Without them
          // the first moveend has nothing to compare against and counts as a
          // movement — and on a cold session the first moveend is often the
          // keyboard resize itself, so the refetch this guards would still fire.
          cameraMoved(mapRef.current, lastEmittedCameraRef);
          rememberBounds(mapRef.current, lastEmittedBoundsRef);
          onVisibleBoundsChangeRef.current({
            sw: [initialBounds.getWest(), initialBounds.getSouth()],
            ne: [initialBounds.getEast(), initialBounds.getNorth()],
          });
        }

      } catch (error) {
        console.error('Error during map load:', error);
        setMapError('Map failed to load properly. Please try refreshing the page.');
      }
    });

    const mapInstance = mapRef.current;
    const mapContainer = mapContainerRef.current;
    let resizeObserver: ResizeObserver | null = null;
    let handleContextLost: ((event: Event) => void) | null = null;
    let handleContextRestored: (() => void) | null = null;

    if (mapInstance && mapContainer) {
      resizeObserver = new ResizeObserver(() => {
        mapInstance.resize();
      });
      resizeObserver.observe(mapContainer);

      // Recover from WebGL context loss (seen on some mobile devices).
      const canvas = mapInstance.getCanvas();
      handleContextLost = (event: Event) => {
        if (typeof (event as any).preventDefault === 'function') {
          (event as any).preventDefault();
        }
        setTimeout(() => {
          if (!mapRef.current) return;
          try {
            // Reuse the URL — passing a circular style object would throw.
            if (styleUrlRef.current) {
              mapRef.current.setStyle(styleUrlRef.current);
              const refetchOnce = () => {
                // 'styledata' fires several times while the new style loads;
                // restoring against a half-loaded style would silently drop the
                // layers, so wait (still listening) for the loaded one.
                if (!mapRef.current?.isStyleLoaded()) return;
                try {
                  // Re-apply the off-market source/layers from the authoritative
                  // data (parent-owned prop or a self-owned re-fetch) — the bare
                  // /buildings-in-bounds fetch ignored the parent's data.
                  restoreOffMarketLayers.current();
                  // setStyle() also wiped consumer-added sources/layers (e.g. the
                  // NewLandingPage demand overlays); re-fire onMapReady so they reattach.
                  onMapReadyRef.current?.(mapRef.current);
                  mapRef.current.off('styledata', refetchOnce);
                } catch (err) {
                  // ignore
                }
              };
              mapRef.current.on('styledata', refetchOnce);
            }
            mapRef.current.resize();
          } catch (err) {
            console.error('Failed to recover WebGL context', err);
          }
        }, 0);
      };
      handleContextRestored = () => {
        try {
          mapInstance.resize();
        } catch (err) {
          // no-op
        }
      };
      canvas.addEventListener('webglcontextlost', handleContextLost as EventListener, false);
      canvas.addEventListener('webglcontextrestored', handleContextRestored as EventListener, false);
    }

    if (mapRef.current) {
      mapRef.current.on('error', (e) => {
        // setFeatureState() races during init/teardown can fire
        //   "The source 'X' does not exist in the map's style."
        // Benign — feature-state would no-op anyway.
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const msg = String((e as any)?.error?.message ?? '');
        if (msg.includes('does not exist in the map')) {
          return;
        }
        console.error('Map error:', e);
        setMapError('An error occurred while loading the map. Please try refreshing the page.');
      });
    }

    return () => {
      console.log('Cleaning up map effect');
      // Drop click and building-pick lookups of this map once it is gone.
      reverseGeocodeRequestIdRef.current += 1;
      resizeObserver?.disconnect();
      if (animationFrameId.current) {
        cancelAnimationFrame(animationFrameId.current);
        animationFrameId.current = undefined;
      }

      const sched = heartScheduleRef.current;
      playLikeAnimationRef.current = null;
      if (sched.nextTimer !== null) window.clearTimeout(sched.nextTimer);
      sched.nextTimer = null;
      sched.deathTimers.forEach((t) => window.clearTimeout(t));
      sched.deathTimers.clear();
      // Stop the self-rescheduling idle sprinkle so it doesn't fire (and re-arm)
      // after the map is gone. (The moveend refire debounce is guarded by the
      // mapRef null-check, so it no-ops post-unmount.)
      if (heartIdleTimerRef.current !== null) {
        window.clearTimeout(heartIdleTimerRef.current);
        heartIdleTimerRef.current = null;
      }
      if (styleRestoreTimerRef.current !== null) {
        window.clearTimeout(styleRestoreTimerRef.current);
        styleRestoreTimerRef.current = null;
      }

      // React 18 disallows synchronous unmount during render.
      heartMarkersRef.current.forEach((entry) => {
        try {
          entry.marker.remove();
        } catch {
          // ignore
        }
        const r = entry.root;
        queueMicrotask(() => {
          try {
            r.unmount();
          } catch {
            // ignore
          }
        });
      });
      heartMarkersRef.current.clear();

      if (initialResolveTimerRef.current !== null) {
        window.clearTimeout(initialResolveTimerRef.current);
        initialResolveTimerRef.current = null;
      }

      if (calloutMarkerRef.current) {
        try {
          calloutMarkerRef.current.remove();
        } catch {
          // ignore
        }
        calloutMarkerRef.current = null;
      }
      const calloutRoot = calloutRootRef.current;
      if (calloutRoot) {
        calloutRootRef.current = null;
        queueMicrotask(() => {
          try {
            calloutRoot.unmount();
          } catch {
            // ignore
          }
        });
      }

      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
      }

      if (mapInstance) {
        try {
          const canvas = mapInstance.getCanvas();
          if (handleContextLost) {
            canvas.removeEventListener('webglcontextlost', handleContextLost as EventListener, false);
          }
          if (handleContextRestored) {
            canvas.removeEventListener('webglcontextrestored', handleContextRestored as EventListener, false);
          }
        } catch (err) {
          // ignore
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  setTimeout(() => {
    if (zoom === 15.5) {
      setZoom(18);
    }
  }, 3000);

  useEffect(() => {
    if (!mapRef.current) return;

    if (triggeredFromSearch) {
      if (searchResultBounds) {
        const mapboxBounds: [[number, number], [number, number]] = [
          searchResultBounds.sw,
          searchResultBounds.ne,
        ];
        const cap = searchResultBounds.maxZoom ?? 18;
        try {
          const naturalCamera = mapRef.current.cameraForBounds(mapboxBounds, {
            padding: 0,
          });
          const lngSpan = searchResultBounds.ne[0] - searchResultBounds.sw[0];
          const latSpan = searchResultBounds.ne[1] - searchResultBounds.sw[1];
          console.log('[BuildingMap search fitBounds]', {
            sw: searchResultBounds.sw,
            ne: searchResultBounds.ne,
            lngSpan,
            latSpan,
            maxZoomCap: cap,
            naturalZoom: naturalCamera?.zoom ?? null,
            cappedZoom: Math.min(naturalCamera?.zoom ?? cap, cap),
            mapContainerSize: {
              width: mapRef.current.getContainer().clientWidth,
              height: mapRef.current.getContainer().clientHeight,
            },
          });
        } catch (logErr) {
          console.warn('[BuildingMap search fitBounds] cameraForBounds failed', logErr);
        }
        mapRef.current.fitBounds(
          mapboxBounds,
          {
            maxZoom: cap,
            padding: 0,
            duration: 1750,
            essential: true,
          },
        );
      } else {
        mapRef.current.flyTo({
          center: [mapCenter.lng, mapCenter.lat],
          essential: true,
          zoom,
        });
      }
      setTriggeredFromSearch(false);
    } else if (recenterInstantRef.current) {
      // Add-my-place: jump straight to the picked building (no fly animation),
      // optionally snapping to a framing zoom so the footprint is queryable.
      mapRef.current.jumpTo({
        center: [mapCenter.lng, mapCenter.lat],
        ...(recenterZoomRef.current !== undefined
          ? { zoom: Math.max(mapRef.current.getZoom(), recenterZoomRef.current) }
          : {}),
      });
    } else {
      mapRef.current.flyTo({
        center: [mapCenter.lng, mapCenter.lat],
        essential: true,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mapCenter]);

  useEffect(() => {
    if (prevSelectedBuildingRef.current && !selectedBuilding) {
      // When parent supplies offMarketFeatureCollection it owns source data;
      // calling /buildings-in-bounds here would clobber it with stripped
      // features and make the off-market icons disappear after closing the
      // building modal.
      if (offMarketFeatureCollection === undefined) {
        fetchBuildingsWithinBounds(mapRef, (counts) => onVisibleCountsChangeRef.current?.(counts));
      }

      if (isSmallMap && mapRef.current) {
        setTimeout(() => {
          if (mapRef.current && mapContainerRef.current) {
            const containerRect = mapContainerRef.current.getBoundingClientRect();
            if (containerRect.width > 0 && containerRect.height > 0) {
              mapRef.current.resize();

              mapRef.current.triggerRepaint?.();

              if (typeof mapRef.current.getCanvas === 'function') {
                const canvas = mapRef.current.getCanvas();
                if (canvas) {
                  // Force reflow.
                  // eslint-disable-next-line @typescript-eslint/no-unused-expressions
                  canvas.offsetHeight;
                }
              }

              // Last-resort recovery for some mobile browsers — reload style
              // so layers re-initialize after modal transitions. Gated behind a
              // "map is actually broken" check: an unconditional setStyle on
              // every building-view close wipes the off-market source/layers
              // (the buy/sell listings would vanish ~150ms after returning).
              // The resize()+repaint above already handle the common sizing
              // recovery; only fall back to the nuclear reload when the style
              // failed to load or its layers were genuinely dropped.
              // "Broken" must hold across TWO checks spaced well apart: a
              // single early isStyleLoaded() snapshot is transiently false on
              // a healthy map whenever any style work is in flight, and a
              // misfired setStyle wipes the layers mid-placement — the
              // uncaught continuePlacement TypeError of JAA-357.
              try {
                smallMapRecoveryNonceRef.current += 1;
                const nonceAtStart = smallMapRecoveryNonceRef.current;
                /** True while the style is missing or lost its hover layer — the reload-worthy state. */
                const mapLooksBroken = () => {
                  const map = mapRef.current;
                  return (
                    !!map &&
                    (!map.isStyleLoaded() || !map.getLayer('building-hover-outline'))
                  );
                };
                setTimeout(() => {
                  if (!mapRef.current) return;
                  if (nonceAtStart !== smallMapRecoveryNonceRef.current) return;
                  if (!mapLooksBroken()) return;
                  setTimeout(() => {
                    const map = mapRef.current;
                    if (!map) return;
                    if (nonceAtStart !== smallMapRecoveryNonceRef.current) return;
                    if (!mapLooksBroken()) return;
                    if (styleUrlRef.current) {
                      map.setStyle(styleUrlRef.current);
                      const refetchOnce = () => {
                        // Same as the WebGL-recovery path above: 'styledata' fires
                        // repeatedly during the reload, and only the loaded style
                        // can hold the restored layers.
                        if (!mapRef.current?.isStyleLoaded()) return;
                        try {
                          // Re-apply the off-market source/layers from the
                          // authoritative data instead of a bare default-params
                          // /buildings-in-bounds fetch, then re-add the hover layer.
                          restoreOffMarketLayers.current();
                          addHoverLayer(mapRef, 'building', 'building-hover-outline');
                          // setStyle() also wiped consumer-added sources/layers (e.g. the
                          // NewLandingPage demand overlays); re-fire onMapReady so they reattach.
                          onMapReadyRef.current?.(mapRef.current);
                          mapRef.current.off('styledata', refetchOnce);
                        } catch (err) {
                          // ignore
                        }
                      };
                      map.on('styledata', refetchOnce);
                      map.resize();
                    }
                  }, 600);
                }, 50);
              } catch (err) {
                // ignore
              }
            }
          }
        }, 100);
      } else if (isLandingPage && mapRef.current) {
        addHoverLayer(mapRef, 'building', 'building-hover-outline');
        // Desktop "View Map" / modal close: the map container may have been
        // resized while the building view was open, leaving Mapbox's canvas at
        // a stale size → it paints white until told its size changed. Resize +
        // repaint once the close has settled (no-op if the size is unchanged).
        setTimeout(() => {
          if (!mapRef.current || !mapContainerRef.current) return;
          const rect = mapContainerRef.current.getBoundingClientRect();
          if (rect.width > 0 && rect.height > 0) {
            mapRef.current.resize();
            mapRef.current.triggerRepaint?.();
          }
        }, 120);
      }
    }
    prevSelectedBuildingRef.current = selectedBuilding;
  }, [selectedBuilding, isSmallMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;
    // Simplified (Add-my-place) maps never fetch listings, so the
    // `clicked-buildings` source — and thus the buy/sell icon + jaakko-outline
    // layers — is never created.
    if (offMarketFeatureCollection !== undefined || simplifiedMap) return undefined;

    const doFetch = () => {
      try {
        fetchBuildingsWithinBounds(mapRef, (counts) => onVisibleCountsChangeRef.current?.(counts));
      } catch (err) {
        // ignore
      }
    };

    // Same rule as the listings refetch: a container resize fires moveend with
    // the camera untouched, and on mobile that is every soft-keyboard open.
    const fetchIfMoved = () => {
      if (mapRef.current && !cameraMoved(mapRef.current, lastFetchedCameraRef)) return;
      doFetch();
    };

    const performInitialFetch = () => {
      // Seed the snapshot from the camera this first fetch belongs to, so the
      // next moveend has something to compare against.
      if (mapRef.current) cameraMoved(mapRef.current, lastFetchedCameraRef);
      doFetch();
      map.on('moveend', fetchIfMoved);
    };

    if (map.isStyleLoaded()) {
      performInitialFetch();
    } else {
      map.once('load', performInitialFetch);
    }

    return () => {
      try {
        map.off('moveend', fetchIfMoved);
        map.off('load', performInitialFetch);
      } catch (err) {
        // ignore
      }
    };
  }, [offMarketFeatureCollection, simplifiedMap]);

  useEffect(() => {
    const map = mapRef.current;
    if (offMarketFeatureCollection === undefined || !map) return undefined;

    const data = offMarketFeatureCollection || {
      type: 'FeatureCollection',
      features: [],
    };
    const updateSource = () => {
      upsertClickedBuildingsSource(mapRef, data);
    };

    // The glow animation keeps repainting even an empty listing source, so
    // idle may never fire. A ready render can apply the payload without a pan.
    return whenMapStyleReady(map, updateSource);
  }, [offMarketFeatureCollection]);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return undefined;

    const handleDragEnd = () => {
      onUserMapDragEndRef.current?.();
      onUserMapInteractionRef.current?.();
    };

    const handleZoomEnd = () => {
      onUserMapInteractionRef.current?.();
    };

    const handleMoveStart = () => {
      onMapMoveStartRef.current?.();
    };

    map.on('dragend', handleDragEnd);
    map.on('zoomend', handleZoomEnd);
    map.on('movestart', handleMoveStart);

    return () => {
      try {
        map.off('dragend', handleDragEnd);
        map.off('zoomend', handleZoomEnd);
        map.off('movestart', handleMoveStart);
      } catch (err) {
        // ignore
      }
    };
  }, []);

  const animateGlow = () => {
    if (!mapRef.current) {
      animationFrameId.current = undefined;
      return;
    }

    if (!mapRef.current.getLayer('building-jaakko-outline')) {
      animationFrameId.current = undefined;
      return;
    }

    animationProgressRef.current += 0.02;

    const lineWidth =
      1.4 + 1.4 * Math.abs(Math.sin(animationProgressRef.current));
    mapRef.current.setPaintProperty(
      'building-jaakko-outline',
      'line-width',
      lineWidth,
    );

    const colorIntensity =
      60 + 40 * Math.abs(Math.sin(animationProgressRef.current));
    const color = `hsl(47, 92%, ${colorIntensity}%)`;
    mapRef.current.setPaintProperty(
      'building-jaakko-outline',
      'line-color',
      color,
    );

    animationFrameId.current = requestAnimationFrame(animateGlow);
  };

  const hideZoomHint = () => {
    setShowZoomHint(false);
    localStorage.setItem('showZoomHint', 'false');
  };

  const hideClickBuildingHint = () => {
    setShowClickBuildingHint(false);
    localStorage.setItem('showClickBuildingHint', 'false');
  };

  const hideClickJaakkoHint = () => {
    setShowClickJaakkoHint(false);
    localStorage.setItem('showClickJaakkoHint', 'false');
  };

  useEffect(() => {
    if (mapRef.current) {
      mapRef.current.resize();
    }
  }, [feedIsOpen, mobileFeedIsOpen]);

  useEffect(() => {
    if (!mapRef.current) return;

    const updateCountryFromMapCenter = () => {
      if (!mapRef.current) return;

      const center = mapRef.current.getCenter();

      const geocodingUrl = `https://api.mapbox.com/geocoding/v5/mapbox.places/${center.lng},${center.lat}.json?access_token=${process.env.REACT_APP_MAPBOX_ACCESS_TOKEN}&types=country`;
      fetch(geocodingUrl)
        .then(response => response.json())
        .then(data => {
          if (data.features && data.features.length > 0) {
            const countryName = data.features[0].text;

            if (countryName && (countryName !== countryOfFeedContent)) {
              setCountryOfFeedContent(countryName);
            }
          }
        })
        .catch(error => {
          console.error('Error determining country from map center:', error);
        });
    };

    const handleMoveEnd = () => {
      setLastMapBoundaryBoxCenter({
        lat: mapRef.current?.getCenter().lat,
        lng: mapRef.current?.getCenter().lng,
      });
      const currentZoom = mapRef.current?.getZoom();
      setLastZoomLevel(currentZoom);
      if (currentZoom < 10.5) {
        updateCountryFromMapCenter();
      }
      // Resizing the container fires the whole move sequence even though the
      // camera never moved, and the on-screen keyboard resizes the viewport
      // every time it appears — so tapping the search field and dismissing it
      // refetched the listings twice over. Three things have to hold before the
      // refetch is skipped: the camera stood still, nothing new came into view,
      // and a text field was involved. That last one is what keeps a DELIBERATE
      // resize honest: dragging a window smaller also leaves the camera still,
      // but the listings and counts describe the visible area, so they have to
      // be refetched for it. Only the keyboard gets to keep the old ones, which
      // is what the ticket asks for.
      if (mapRef.current) {
        const keyboardEpisode = aTextFieldHasFocus();
        const stillCamera = !cameraMoved(mapRef.current, lastEmittedCameraRef);
        // Skipping must NOT update the remembered area: the keyboard shrinks the
        // view and then restores it, and if the shrunken view were remembered
        // the restore would look like new map and refetch after all.
        const covered = viewIsCovered(mapRef.current, lastEmittedBoundsRef);
        if (stillCamera && covered && (keyboardEpisode || inKeyboardShrinkRef.current)) {
          // The keyboard opening is recognised by the text field that summoned
          // it; its closing by this flag, which stays set until the view stops
          // being a crop of what we are showing. A deliberate resize satisfies
          // neither and refetches, because the listings and the counts describe
          // the visible area.
          // The episode ends when the view is back to the whole area again —
          // otherwise the flag would swallow the next deliberate resize too.
          inKeyboardShrinkRef.current = !viewIsBackToRemembered(mapRef.current, lastEmittedBoundsRef);
          // movestart already told the caller a movement was under way, and it
          // put a loading state on screen that only a completed fetch clears.
          // No fetch is coming, so say so — otherwise the skeleton never lifts.
          onMapMoveDiscardedRef.current?.();
          return;
        }
        inKeyboardShrinkRef.current = false;
        rememberBounds(mapRef.current, lastEmittedBoundsRef);
      }
      if (onVisibleBoundsChangeRef.current && mapRef.current) {
        const moveBounds = mapRef.current.getBounds();
        onVisibleBoundsChangeRef.current({
          sw: [moveBounds.getWest(), moveBounds.getSouth()],
          ne: [moveBounds.getEast(), moveBounds.getNorth()],
        });
      }
    };

    mapRef.current.on('moveend', handleMoveEnd);

    return () => {
      if (mapRef.current) {
        mapRef.current.off('moveend', handleMoveEnd);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryOfFeedContent]);

  return (
    <div
      className={`map-wrapper ${feedIsOpen ? 'map-wrapper--feed-open' : ''} ${isLandingPage ? 'map-wrapper--landing-page-map' : ''} ${hideGeolocateControl ? 'map-wrapper--hide-geolocate-control' : ''}`}
      key={mobileFeedIsOpen ? 'feed-open' : 'feed-closed'}
    >
      {mapError ? (
        <div className="map-error-fallback">
          <div className="map-error-fallback__content">
            <div className="map-error-fallback__icon">
              <svg
                width="48"
                height="48"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>
            <div className="map-error-fallback__title">
              <Trans i18nKey="map.error.title">Map unavailable</Trans>
            </div>
            <div className="map-error-fallback__message">
              {mapError}
            </div>
            <button
              className="map-error-fallback__retry-button"
              onClick={() => {
                setMapError(null);
                window.location.reload();
              }}
            >
              <Trans i18nKey="map.error.retry">Refresh page</Trans>
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="map-hints">
            {geolocateHintKey && (
              <button
                type="button"
                className="map-location-accuracy-hint"
                onClick={hideGeolocateHint}
              >
                <span>
                  <Trans i18nKey={geolocateHintKey}>
                    Your browser shared an approximate location, so the map may
                    be less exact.
                  </Trans>
                </span>
                <img
                  src="/assets/close.svg"
                  alt="Close"
                  height="18px"
                />
              </button>
            )}
            <div
              className="jaakko-info"
              onClick={() => {
                setShowJaakkoSpeechBubble(!showJaakkoSpeechBubble);
                TagManager.dataLayer({
                  dataLayer: {
                    event: 'jaakko_info_clicked',
                    category: 'User Interaction',
                    action: 'Click',
                    label: 'Jaakko Info Clicked',
                  },
                });
              }}
              ref={jaakkoInfoRef}
            >
              <img
                src="/assets/info_jaakko.png"
                alt="Jaakko info"
                height="60px"
              />
            </div>
          </div>

          {!feedIsOpen && (
            <button
              className="map-feed-vertical-button"
              onClick={() => setShowFeed(true)}
            >
              <div className="map-feed-vertical-button__text">
                <Trans i18nKey="feed.button.showFeed">
                  Fiidi
                </Trans>
              </div>
              <div className="map-feed-vertical-button__icon">
                <RightArrow />
              </div>
            </button>
          )}

          <div
            ref={mapContainerRef}
            id="map-container"
            className="map-container"
            style={{ opacity: hideLabels && !labelsHidden ? 0 : 1 /*, transition: 'opacity 240ms ease' */ }}
          ></div>
        </>
      )}
    </div>
  );
});

BuildingMap.displayName = 'BuildingMap';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isFiniteNum = (n: any) => typeof n === 'number' && Number.isFinite(n);
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const isValidLngLat = (lng: any, lat: any) =>
  isFiniteNum(lng) && isFiniteNum(lat) && lng >= -180 && lng <= 180 && lat >= -90 && lat <= 90;
// Validate every coordinate pair — malformed data can crash WebGL.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const validateGeometryCoords = (geometry: any): boolean => {
  try {
    const polygons = geometry.type === 'Polygon' ? [geometry.coordinates] : geometry.coordinates;
    for (const poly of polygons) {
      for (const ring of poly) {
        for (const pair of ring) {
          if (!isValidLngLat(pair[0], pair[1])) return false;
        }
      }
    }
    return true;
  } catch {
    return false;
  }
};

// Gather every rendered slice of a building (by Mapbox feature id) and union
// them into a single Polygon/MultiPolygon footprint, removing the internal
// borders that tile splits introduce. Falls back to the passed geometry.
const mergeBuildingFootprint = (
  map: mapboxgl.Map,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  featureId: any,
  fallbackGeometry?: GeoJSON.Geometry | null,
): GeoJSON.Geometry | null => {
  let mergedCoordinates: number[][][] = [];
  try {
    const slices = map.queryRenderedFeatures({
      layers: ['building'],
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      filter: ['==', ['id'], featureId as any],
    });
    slices.forEach((slice) => {
      if (slice.geometry?.type === 'Polygon') {
        mergedCoordinates.push(...(slice.geometry.coordinates as number[][][]));
      } else if (slice.geometry?.type === 'MultiPolygon') {
        (slice.geometry.coordinates as number[][][][]).forEach((poly) => {
          mergedCoordinates.push(...poly);
        });
      }
    });
  } catch (err) {
    // ignore failures when querying rendered features
  }

  if (mergedCoordinates.length === 0 && fallbackGeometry?.type === 'Polygon') {
    mergedCoordinates = (fallbackGeometry as GeoJSON.Polygon).coordinates as number[][][];
  }
  if (mergedCoordinates.length === 0) return null;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  let buildingGeometry: any;
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const pc = require('polygon-clipping');
    const unionResult: number[][][][] = pc.union(
      ...mergedCoordinates.map((ring) => [ring]),
    );
    buildingGeometry = unionResult.length === 1
      ? { type: 'Polygon', coordinates: unionResult[0] }
      : { type: 'MultiPolygon', coordinates: unionResult };
  } catch (err) {
    buildingGeometry = mergedCoordinates.length > 1
      ? { type: 'MultiPolygon', coordinates: mergedCoordinates.map((ring) => [ring]) }
      : { type: 'Polygon', coordinates: mergedCoordinates };
  }

  return validateGeometryCoords(buildingGeometry) ? buildingGeometry : null;
};

// How far to pull a building click toward the footprint center before reverse
// geocoding: 0 = raw click point, 1 = centroid. A small inward nudge keeps the
// clicked side (so multi-address buildings resolve correctly) while avoiding
// the neighbour's address at the edges.
const CENTROID_NUDGE = 0.3;

// Average of the outer rings — a stable point to anchor the callout marker.
const geometryCentroid = (geometry: GeoJSON.Geometry): [number, number] | null => {
  const outerRings: number[][][] = [];
  if (geometry.type === 'Polygon') {
    outerRings.push(geometry.coordinates[0] as number[][]);
  } else if (geometry.type === 'MultiPolygon') {
    (geometry.coordinates as number[][][][]).forEach((poly) => {
      if (poly[0]) outerRings.push(poly[0] as number[][]);
    });
  } else {
    return null;
  }
  let sx = 0;
  let sy = 0;
  let n = 0;
  outerRings.forEach((ring) => {
    ring.forEach((pair) => {
      if (isFiniteNum(pair[0]) && isFiniteNum(pair[1])) {
        sx += pair[0];
        sy += pair[1];
        n += 1;
      }
    });
  });
  return n === 0 ? null : [sx / n, sy / n];
};

// Resolve the complete footprint of the building at a coordinate. When nothing
// is rendered exactly under the point, fall back to the nearest footprint in a
// small pixel box around it (rare — the map opens centered on the address).
const findBuildingFootprintAtCoord = (
  map: mapboxgl.Map,
  target: { lng: number; lat: number },
): GeoJSON.Geometry | null => {
  if (!map.getLayer('building')) return null;
  const p = map.project([target.lng, target.lat]);
  let chosen: mapboxgl.MapboxGeoJSONFeature | undefined = map.queryRenderedFeatures(p, {
    layers: ['building'],
  })[0];
  if (!chosen) {
    const R = 80;
    const near = map.queryRenderedFeatures(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      [[p.x - R, p.y - R], [p.x + R, p.y + R]] as any,
      { layers: ['building'] },
    );
    let best: { f: mapboxgl.MapboxGeoJSONFeature; d: number } | null = null;
    near.forEach((f) => {
      const c = f.geometry ? geometryCentroid(f.geometry as GeoJSON.Geometry) : null;
      if (!c) return;
      const dx = c[0] - target.lng;
      const dy = c[1] - target.lat;
      const d = dx * dx + dy * dy;
      if (!best || d < best.d) best = { f, d };
    });
    chosen = best?.f;
  }
  if (!chosen || chosen.id === undefined || chosen.id === null) return null;
  return mergeBuildingFootprint(map, chosen.id, chosen.geometry as GeoJSON.Geometry);
};

/**
 * Resolves a map click into a building selection. Bare buildings (polygon hits
 * and `building-clickable-dots`) are selected by
 * performReverseGeocodingAndFetchPOIs with the geocoder's first address, or,
 * when it has none, with only their district (JAA-417). With `openImmediately`,
 * LISTING markers (sell/buy icons and the sell dot) additionally open at once
 * with the bare marker data and are enriched after the geocode, and every
 * building opens as soon as its address resolves rather than after the POI
 * tilequery.
 * With `onFootprint` (Add my place) the caller resolves the address itself.
 */
export const handleOnClickBuildingShowBuildingPage = (
  e: MapMouseEvent,
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  selectBuilding: (building: BuildingWithConvertedAddresses) => void,
  onFootprint?: (
    geometry: GeoJSON.Geometry | null,
    atLngLat: { lng: number; lat: number },
    buildingId: string,
  ) => void,
  // When true, fire selectBuilding immediately for listing markers only, with
  // the bare marker data so the preview opens at once; geocoding then enriches
  // it. Off for full-view flows.
  openImmediately = false,
) => {
  const map = mapRef.current!;

  // At low zoom the composite `building` polygon hit-test returns empty even
  // when an icon is visible. Try icon layers first — they carry building_id
  // directly, so we can skip the polygon query.
  const iconLayers = [
    'building-off-market-sell-icon',
    'building-off-market-sell-dot',
    'building-off-market-buy-icon',
    'building-clickable-dots',
  ].filter((id) => map.getLayer(id));
  const iconHit = iconLayers.length
    ? map.queryRenderedFeatures(e.point, { layers: iconLayers })
    : [];
  if (iconHit.length > 0) {
    const f = iconHit[0];
    const isListingIcon = f.layer?.id !== 'building-clickable-dots';
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const bid = String((f.properties as any)?.building_id ?? f.id ?? '');
    if (bid) {
      // "Add my place": highlight the footprint + anchor the callout; the AMP
      // handler resolves the address itself (skip the shared geocode/POI fetch).
      if (onFootprint) {
        onFootprint(
          findBuildingFootprintAtCoord(map, { lng: e.lngLat.lng, lat: e.lngLat.lat }),
          { lng: e.lngLat.lng, lat: e.lngLat.lat },
          bid,
        );
        return;
      }
      // If the footprint is already rendered (user zoomed in to the icon), grab
      // the whole polygon straight from the loaded tiles — fast, no backend call.
      // Falls back to empty so the hero resolves by id from the backend when the
      // footprint isn't on screen (low zoom).
      const fp = footprintFromRenderedMap(map, Number(bid));
      const building: BuildingWithConvertedAddresses = {
        id: bid,
        centroid: { lat: e.lngLat.lat, lng: e.lngLat.lng },
        addresses: [],
        geometry: (fp ?? { type: 'Polygon', coordinates: [] }) as BuildingWithConvertedAddresses['geometry'],
        // Tag only a real captured footprint so the hero trusts it; an empty
        // placeholder stays untagged and the hero resolves via /building-geometry.
        geometry_source: fp ? 'mapbox-tiles' : undefined,
        properties: { name: '', addresses: [] },
        pois: [],
      };
      // Remember the click point so a cold BARE link to this building can be
      // located later (id->centroid index fallback).
      recordBuildingCentroid(building.id, e.lngLat.lat, e.lngLat.lng);
      // Only listing markers open before the geocode; plain dots wait for it.
      if (openImmediately && isListingIcon) selectBuilding(building);
      performReverseGeocodingAndFetchPOIs(
        building,
        selectBuilding,
        e.lngLat,
        openImmediately,
        !isListingIcon,
      );
      return;
    }
  }

  const buildingFeatures = map.queryRenderedFeatures(e.point, {
    layers: ['building'],
  });

  if (buildingFeatures.length > 0) {
    const buildingFeature = buildingFeatures[0];

    // "Add my place": highlight the footprint + anchor the callout; the AMP
    // handler resolves the address itself (skip the shared geocode/POI fetch).
    if (onFootprint) {
      onFootprint(
        mergeBuildingFootprint(
          map,
          buildingFeature.id,
          buildingFeature.geometry as GeoJSON.Geometry,
        ),
        { lng: e.lngLat.lng, lat: e.lngLat.lat },
        buildingFeature.id.toString(),
      );
      return;
    }

    const building: BuildingWithConvertedAddresses = {
      id: buildingFeature.id.toString(),
      centroid: { lat: e.lngLat.lat, lng: e.lngLat.lng },
      addresses: [],
      geometry: { type: 'Polygon', coordinates: [] },
      properties: {
        name: '',
        addresses: [],
      },
      pois: [],
    };
    // Reconstruct the full footprint from the rendered tiles by id (unions
    // tile-split slices); falls back to this feature's own geometry.
    const buildingGeometry = footprintFromRenderedMap(
      mapRef.current!,
      buildingFeature.id,
      buildingFeature.geometry,
    );
    if (buildingGeometry) {
      building.geometry = buildingGeometry as BuildingWithConvertedAddresses['geometry'];
      building.geometry_source = 'mapbox-tiles';
    }

    // Remember the click point so a cold BARE link to this building can be
    // located later (id->centroid index fallback).
    recordBuildingCentroid(building.id, e.lngLat.lat, e.lngLat.lng);
    performReverseGeocodingAndFetchPOIs(
      building,
      selectBuilding,
      e.lngLat,
      openImmediately,
      true,
    );
  } else {
    console.log('No building found at this location');
  }
};

const fetchBuildingsWithinBounds = (
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  onVisibleCounts?: (counts: { sellers: number; buyers: number }) => void,
) => {
  const bounds = mapRef.current!.getBounds();
  if (!bounds) return;
  // /off-market-listings-in-bounds expects the bbox nested under `bounds`
  // (unlike the flat bbox /buildings-in-bounds took). listingType / sortCenter
  // / filters are omitted so the backend applies its defaults ('both', viewport
  // center) — this map has no search UI to thread those through.
  const body = {
    bounds: {
      sw: [bounds.getWest(), bounds.getSouth()],
      ne: [bounds.getEast(), bounds.getNorth()],
    },
  };

  fetch(`${process.env.REACT_APP_API_URL}/off-market-listings-in-bounds`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-TOKEN': getCookie('csrf_access_token') || '',
    },
    credentials: 'include',
    body: JSON.stringify(body),
  })
    .then((response) => response.json())
    .then((data) => {
      if (onVisibleCounts) {
        const visibleCounts = (data && data.visibleCounts) || {};
        const sellers = Number.isFinite(visibleCounts.sellers) ? visibleCounts.sellers : 0;
        const buyers = Number.isFinite(visibleCounts.buyers) ? visibleCounts.buyers : 0;
        onVisibleCounts({ sellers, buyers });
      }

      // Before aggregating: upsertClickedBuildingsSource stamps is_seen from
      // this set, so adopting it later would render one frame of yellow on
      // markers the viewer has already clicked.
      setServerSeenBuildings(data.seenBuildingIds);

      // JAA-253: mapFeatures are per-listing; collapse to one feature per
      // building client-side so marker counts react to FE-only state without a
      // refetch. Mirrors what NewLandingPage feeds via offMarketFeatureCollection.
      const collection = aggregateBuildingFeatures(data.mapFeatures);
      upsertClickedBuildingsSource(mapRef, collection);
    })
    .catch((error) => {
      console.error('Error fetching off-market listings:', error);
    });
};

// Without this cache, tile splits return different polygon-halves per zoom,
// causing the computed polylabel — and thus the icon — to visibly drift.
const polylabelCache: Map<string, [number, number]> = new Map();

type MapboxExpr = mapboxgl.Expression;
const expr = <T,>(e: T): MapboxExpr => e as unknown as MapboxExpr;

// Mapbox auto-coerces purely-numeric GeoJSON feature.id strings to Number on
// load. setFeatureState matches by strict type, so mirror that coercion here.
const buildingIdAsMapboxId = (bid: string): string | number =>
  /^\d+$/.test(bid) ? Number(bid) : bid;

const DESKTOP_BREAKPOINT_PX = 960;
const isDesktop = (): boolean =>
  typeof window !== 'undefined' && window.innerWidth >= DESKTOP_BREAKPOINT_PX;

// Lazy runtime icon generation for sell counts >=10. Mirrors build-seller-icons.py
// build_seller_multi / build_seller_new_multi but renders via <canvas> in the
// browser. Generated PNGs are added to the Mapbox style under
// `off-market-sell-icon-multi-${count}${suffix}` and reused for all features
// with the same count + state.

// Pill geometry — keep in sync with build-seller-icons.py.
const SELLER_GEOM = {
  HOME_SIZE: 26,
  EDGE_PAD: 9,
  NUM_SLOT_1CHAR: 14,
  NUM_SLOT_2CHAR: 22,
  GAP: 4,
  PAD: 12,
  NEW_BADGE_W: 28,
  NEW_BADGE_H: 14,
};

type SellState =
  | 'default'
  | 'selected'
  | 'new-default'
  | 'new-seen'
  | 'new-selected';

const SELLER_STATE_STYLES: Record<SellState, {
  bg: string;
  border: string;
  text: string;
  badge: boolean;
}> = {
  'default':       { bg: '#FFFFFF', border: '#B3B3B3', text: '#1A1A1A', badge: false },
  'selected':      { bg: '#000000', border: '#000000', text: '#FFFFFF', badge: false },
  'new-default':   { bg: '#FFE033', border: '#AF9A24', text: '#1A1A1A', badge: true },
  'new-seen':      { bg: '#F5F6FA', border: '#B3B3B3', text: '#1A1A1A', badge: true },
  'new-selected':  { bg: '#1A1A1A', border: '#1A1A1A', text: '#FFFFFF', badge: true },
};

// Promise-cached so concurrent setData ticks don't double-generate. Resolved
// values point at the registered Mapbox image id (or null on failure).
const sellMultiIconPromises = new Map<string, Promise<string | null>>();

// Home.png cached as HTMLImageElement after first load; same source as the
// pre-rendered PNGs use.
let homeImgPromise: Promise<HTMLImageElement | null> | null = null;
// Ensure Inter Bold is loaded before any canvas rendering — otherwise the
// first frame may fall back to a system font and the digits look subtly
// different from the bundled rsvg-rendered PNGs. Resolves once the font is
// available; idempotent across calls.
let interBoldPromise: Promise<void> | null = null;
const ensureInterBold = (): Promise<void> => {
  if (interBoldPromise) return interBoldPromise;
  interBoldPromise = (async () => {
    try {
      if (typeof document === 'undefined' || !document.fonts) return;
      // Trigger load of the exact face used by ensureSellMultiIcon.
      await document.fonts.load('bold 16px Inter');
      await document.fonts.ready;
    } catch {
      // ignore — canvas will fall back to next family in stack
    }
  })();
  return interBoldPromise;
};

const loadHomeImage = (): Promise<HTMLImageElement | null> => {
  if (homeImgPromise) return homeImgPromise;
  homeImgPromise = new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = `${process.env.PUBLIC_URL}/assets/markers/seller/Home.png`;
  });
  return homeImgPromise;
};

const drawAsymmetricBadgePath = (
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void => {
  // TL/TR/BR rounded (r), BL sharp. Matches Python _new_badge_path.
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x, y + h);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
};

const ensureSellMultiIcon = (
  map: mapboxgl.Map,
  count: number,
  state: SellState,
  locale: IconLocale = currentIconLocale,
): Promise<string | null> => {
  const isNewState = state !== 'default' && state !== 'selected';
  const ls = isNewState ? localeSuffix(locale) : '';
  const suffix =
    state === 'default'      ? '' :
    state === 'selected'     ? '-selected' :
    state === 'new-default'  ? '-new' :
    state === 'new-seen'     ? '-new-seen' :
                               '-new-selected';
  const id = `off-market-sell-icon-multi-${count}${suffix}${ls}`;
  if (map.hasImage(id)) return Promise.resolve(id);
  const cached = sellMultiIconPromises.get(id);
  if (cached) return cached;

  const promise = (async () => {
    const [home] = await Promise.all([loadHomeImage(), ensureInterBold()]);
    if (!home) return null;
    const { HOME_SIZE, EDGE_PAD, NUM_SLOT_2CHAR, GAP, PAD, NEW_BADGE_W, NEW_BADGE_H } = SELLER_GEOM;
    const PILL_H = HOME_SIZE + EDGE_PAD * 2;
    const PILL_R = PILL_H / 2;
    const label = String(count);
    const num_slot = label.length >= 2 ? NUM_SLOT_2CHAR : SELLER_GEOM.NUM_SLOT_1CHAR;
    const multi_w = EDGE_PAD * 2 + HOME_SIZE + GAP + num_slot;
    const canvas_w = multi_w + PAD * 2 + 6;
    const canvas_h = PILL_H + PAD * 2 + 4;
    const pill_x = PAD;
    const pill_y = PAD + 4;
    const home_x = pill_x + EDGE_PAD;
    const home_y = pill_y + (PILL_H - HOME_SIZE) / 2;
    const text_x = pill_x + EDGE_PAD + HOME_SIZE + GAP + num_slot / 2;
    const text_y = pill_y + PILL_H / 2 + 5;

    const scale = 2;
    const canvas = document.createElement('canvas');
    canvas.width = canvas_w * scale;
    canvas.height = canvas_h * scale;
    const ctx = canvas.getContext('2d');
    if (!ctx) return null;
    ctx.scale(scale, scale);
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = 'high';

    const style = SELLER_STATE_STYLES[state];

    // Pill body — rounded rect via arcTo so corners are crisp.
    const drawRoundedRect = (
      x: number, y: number, w: number, h: number, r: number,
    ) => {
      ctx.beginPath();
      ctx.moveTo(x + r, y);
      ctx.lineTo(x + w - r, y);
      ctx.arcTo(x + w, y, x + w, y + r, r);
      ctx.lineTo(x + w, y + h - r);
      ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
      ctx.lineTo(x + r, y + h);
      ctx.arcTo(x, y + h, x, y + h - r, r);
      ctx.lineTo(x, y + r);
      ctx.arcTo(x, y, x + r, y, r);
      ctx.closePath();
    };
    drawRoundedRect(pill_x, pill_y, multi_w, PILL_H, PILL_R);
    ctx.fillStyle = style.bg;
    ctx.fill();
    ctx.lineWidth = 1;
    ctx.strokeStyle = style.border;
    ctx.stroke();

    // House icon
    ctx.drawImage(home, home_x, home_y, HOME_SIZE, HOME_SIZE);

    // Count text
    ctx.fillStyle = style.text;
    ctx.font = 'bold 16px Inter, Helvetica, Arial, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    ctx.fillText(label, text_x, text_y);

    // "New" badge if applicable
    if (style.badge) {
      const badge_x = pill_x + multi_w - 16;
      const badge_y = pill_y - 4;
      drawAsymmetricBadgePath(ctx, badge_x, badge_y, NEW_BADGE_W, NEW_BADGE_H, 4);
      ctx.fillStyle = '#FF6666';
      ctx.fill();
      ctx.fillStyle = '#FFFFFF';
      ctx.font = 'bold 9px Silka, Inter, Helvetica, Arial, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(resolveBadgeLabel(locale), badge_x + NEW_BADGE_W / 2, badge_y + NEW_BADGE_H / 2 + 3.2);
    }

    const data = ctx.getImageData(0, 0, canvas.width, canvas.height);
    if (!map.hasImage(id)) {
      try {
        map.addImage(id, data, { pixelRatio: scale });
      } catch (err) {
        console.error(`Failed to addImage ${id}:`, err);
        return null;
      }
    }
    map.triggerRepaint();
    return id;
  })();

  sellMultiIconPromises.set(id, promise);
  return promise;
};

const SELL_STATES_TO_GENERATE: ReadonlyArray<SellState> = [
  'default', 'selected', 'new-default', 'new-seen', 'new-selected',
];

// Walk a FeatureCollection, find sell counts >=10, ensure the per-count icons
// exist for every state we render. Resolves when all generations complete.
const ensureRuntimeSellIcons = async (
  map: mapboxgl.Map,
  data: GeoJSON.FeatureCollection,
): Promise<void> => {
  const counts = new Set<number>();
  for (const f of data.features) {
    const c = Number(
      (f.properties as { could_sell_count?: number } | null)?.could_sell_count ?? 0,
    );
    if (c >= 10) counts.add(c);
  }
  if (counts.size === 0) return;
  const tasks: Array<Promise<unknown>> = [];
  counts.forEach((c) => {
    SELL_STATES_TO_GENERATE.forEach((s) => {
      tasks.push(ensureSellMultiIcon(map, c, s));
    });
  });
  await Promise.all(tasks);
};

// ─── sell-marker decluttering (JAA sell-listing map marker change) ─────────
// Per-map opt-in flag, set once at map creation from the `declutterSellMarkers`
// prop. Keyed by map instance (WeakMap, like inflightMarkerImagesByMap) so a
// landing map and a non-decluttered map can coexist without leaking state.
const sellDeclutterByMap = new WeakMap<mapboxgl.Map, boolean>();
const setSellMarkerDecluttering = (map: mapboxgl.Map, enabled: boolean): void => {
  sellDeclutterByMap.set(map, enabled);
};
const isSellDeclutterEnabled = (map: mapboxgl.Map): boolean =>
  sellDeclutterByMap.get(map) === true;

// Collision paddings (screen px around each icon's box) — the "how sparse does
// the map feel" tuning knobs. Bigger = fewer simultaneously visible markers.
// The pill PNGs already carry ~9-12px of transparent canvas padding, which
// also counts toward the collision box.
const SELL_PILL_COLLISION_PADDING = 6;

// Placement priority: lower sell_priority = placed first = survives collision.
// Computed per building in aggregateBuildingFeatures (new < rich < dot bands).
const sellPrioritySortKey: MapboxExpr = expr([
  'to-number', ['get', 'sell_priority'], 0,
]);

// Dots all render, so higher-ranked sellers draw above lower-ranked dots when
// their points coincide. The pill layer keeps the ascending placement key.
const sellPriorityDrawKey: MapboxExpr = expr([
  '-', 0, ['to-number', ['get', 'sell_priority'], 0],
]);

const sellTierIsDot: MapboxExpr = expr(['==', ['get', 'sell_tier'], 'dot']);

const iconSizeExpr: MapboxExpr = expr([
  'interpolate', ['linear'], ['zoom'],
  12, 0.75,
  18, 1.0,
]);

const iconOffsetExpr = (sign: 1 | -1): MapboxExpr => expr([
  'interpolate', ['linear'], ['zoom'],
  12, ['literal', [0, 0]],
  15, ['literal', [10 * sign, 8]],
  18, ['literal', [20 * sign, 16]],
]);

const iconOpacityHover = (active: boolean): MapboxExpr => expr([
  'case',
  ['boolean', ['feature-state', 'hover'], false],
  active ? 1 : 0,
  active ? 0 : 1,
]);

// Sell pill icon-image: count=1 has the three-variant case (new/seen/default);
// counts 2..9 use the 1-digit blank pill; counts 10+ use the 2-digit blank pill.
// The number itself is rendered by a separate text-field overlay (see below).
// Per count-bucket variant. New-state images have a locale suffix (`` for en,
// `-fi` for fi) so the badge text matches the current i18n locale. The base
// (non-new) variants are language-agnostic and stay constant.
type IconLocale = 'en' | 'fi';
const localeSuffix = (l: IconLocale): string => (l === 'fi' ? '-fi' : '');

// Module-level snapshot of the active marker locale. Updated by the i18n
// effect in BuildingMap; read by addLayersInOrder and ensureSellMultiIcon.
let currentIconLocale: IconLocale = 'en';
const setIconLocale = (l: IconLocale): void => {
  currentIconLocale = l;
};
const resolveBadgeLabel = (l: IconLocale): string => (l === 'fi' ? 'Uusi' : 'New');

const sellVariantCase = (
  bucket: string,
  selected: boolean,
  locale: IconLocale,
): MapboxExpr => {
  const ls = localeSuffix(locale);
  return selected
    ? expr([
        'case',
        ['==', ['get', 'is_new'], true],
        `${bucket}-new-selected${ls}`,
        `${bucket}-selected`,
      ])
    : expr([
        'case',
        ['all',
          ['==', ['get', 'is_new'], true],
          ['!=', ['get', 'is_seen'], true],
        ],
        `${bucket}-new${ls}`,
        ['==', ['get', 'is_new'], true],
        `${bucket}-new-seen${ls}`,
        bucket,
      ]);
};

const sellIconImageExpr = (selected: boolean, locale: IconLocale = 'en'): MapboxExpr => {
  const suf = selected ? '-selected' : '';
  const ls = localeSuffix(locale);
  // For counts >=10, render a runtime-generated icon via concat:
  //   off-market-sell-icon-multi-${count}${state-suffix}${locale-suffix}
  // The matching addImage call happens in ensureSellMultiIcon when listings
  // arrive. Mapbox falls back to the default branch of step otherwise.
  const dynamicMulti: MapboxExpr = selected
    ? expr([
        'case',
        ['==', ['get', 'is_new'], true],
        expr([
          'concat', 'off-market-sell-icon-multi-',
          expr(['to-string', ['to-number', ['get', 'could_sell_count'], 0]]),
          `-new-selected${ls}`,
        ]),
        expr([
          'concat', 'off-market-sell-icon-multi-',
          expr(['to-string', ['to-number', ['get', 'could_sell_count'], 0]]),
          '-selected',
        ]),
      ])
    : expr([
        'case',
        ['all',
          ['==', ['get', 'is_new'], true],
          ['!=', ['get', 'is_seen'], true],
        ],
        expr([
          'concat', 'off-market-sell-icon-multi-',
          expr(['to-string', ['to-number', ['get', 'could_sell_count'], 0]]),
          `-new${ls}`,
        ]),
        ['==', ['get', 'is_new'], true],
        expr([
          'concat', 'off-market-sell-icon-multi-',
          expr(['to-string', ['to-number', ['get', 'could_sell_count'], 0]]),
          `-new-seen${ls}`,
        ]),
        expr([
          'concat', 'off-market-sell-icon-multi-',
          expr(['to-string', ['to-number', ['get', 'could_sell_count'], 0]]),
          suf,
        ]),
      ]);
  return expr([
    'step',
    ['to-number', ['get', 'could_sell_count'], 0],
    sellVariantCase('off-market-sell-icon-1', selected, locale),
    2, sellVariantCase('off-market-sell-icon-2', selected, locale),
    3, sellVariantCase('off-market-sell-icon-3', selected, locale),
    4, sellVariantCase('off-market-sell-icon-4', selected, locale),
    5, sellVariantCase('off-market-sell-icon-5', selected, locale),
    6, sellVariantCase('off-market-sell-icon-6', selected, locale),
    7, sellVariantCase('off-market-sell-icon-7', selected, locale),
    8, sellVariantCase('off-market-sell-icon-8', selected, locale),
    9, sellVariantCase('off-market-sell-icon-9', selected, locale),
    10, dynamicMulti,
  ]);
};

const addHoverLayer = (
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  sourceLayer: string,
  layerId: string,
): void => {
  const map = mapRef.current;
  if (!map) return;
  const fillLayerId = `${layerId}-fill`;
  if (!map.getLayer(fillLayerId)) {
    try {
      map.addLayer({
        id: fillLayerId,
        type: 'fill',
        source: 'composite',
        'source-layer': sourceLayer,
        paint: {
          'fill-color': '#00805A',
          'fill-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            0.15,
            0,
          ],
        },
      });
    } catch (e) {
      console.error(`Error adding hover fill ${fillLayerId}:`, e);
    }
  }
  if (!map.getLayer(layerId)) {
    try {
      map.addLayer({
        id: layerId,
        type: 'line',
        source: 'composite',
        'source-layer': sourceLayer,
        paint: {
          'line-color': '#00805A',
          'line-width': 2,
          'line-opacity': [
            'case',
            ['boolean', ['feature-state', 'hover'], false],
            1,
            0,
          ],
        },
      });
    } catch (e) {
      console.error(`Error adding hover layer ${layerId}:`, e);
    }
  }
};

type HoverState = { buildingId: string | null };

// All three sources key feature-state by backend building_id:
//   - clicked-buildings (geojson): id == String(building_id)
//   - building-dots (geojson): id == Number(building_id) when numeric
//   - composite vector tile `building`: feature.id == OSM way id == building_id
// One bid drives sell pill swap, dot swap, AND polygon teal fill.
type HoverSourceDescriptor = { source: string; sourceLayer?: string };
const BUILDING_ID_HOVER_SOURCES: ReadonlyArray<HoverSourceDescriptor> = [
  { source: 'clicked-buildings' },
  { source: 'building-dots' },
  { source: 'composite', sourceLayer: 'building' },
];

const setHoverByBuildingId = (
  map: mapboxgl.Map,
  bid: string | null,
  prev: string | null,
  sources: ReadonlyArray<HoverSourceDescriptor> = BUILDING_ID_HOVER_SOURCES,
): void => {
  const flip = (id: string, hover: boolean) => {
    const mapboxId = buildingIdAsMapboxId(id);
    for (const desc of sources) {
      if (!map.getSource(desc.source)) continue;
      try {
        const target: mapboxgl.FeatureIdentifier = desc.sourceLayer
          ? { source: desc.source, sourceLayer: desc.sourceLayer, id: mapboxId }
          : { source: desc.source, id: mapboxId };
        map.setFeatureState(target, { hover });
      } catch {
        // ignore — id may not exist in every source
      }
    }
  };
  if (prev !== null && prev !== bid) flip(prev, false);
  if (bid !== null && bid !== prev) flip(bid, true);
  // Defensive — some Mapbox builds skip auto-repaint when setFeatureState is
  // called outside a rendering tick.
  try { map.triggerRepaint(); } catch { /* ignore */ }
};

const rewriteListingGeometriesToPolylabel = (
  _map: mapboxgl.Map,
  data: GeoJSON.FeatureCollection,
): GeoJSON.FeatureCollection => {
  const seen = loadSeenNewBuildings();
  return {
    ...data,
    features: data.features.map((f) => {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const bid = String((f.properties as any)?.building_id ?? f.id ?? '');
      if (!bid || !seen.has(bid)) return f;
      return {
        ...f,
        properties: { ...(f.properties || {}), is_seen: true },
      };
    }),
  };
};


const upsertClickedBuildingsSource = (
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
  data: GeoJSON.FeatureCollection,
) => {
  const map = mapRef.current;
  if (!map) return;

  const rewritten = rewriteListingGeometriesToPolylabel(map, data);

  // Generate runtime sell icons (counts >=10) on a best-effort basis. We
  // setData immediately — features with counts >=10 may show as blank icons
  // for one frame until generation completes + triggerRepaint fires.
  void ensureRuntimeSellIcons(map, rewritten);

  if (map.getSource('clicked-buildings')) {
    (
      map.getSource('clicked-buildings') as mapboxgl.GeoJSONSource
    ).setData(rewritten);
    ensureClickedBuildingLayers(mapRef);
    return;
  }

  map.addSource('clicked-buildings', {
    type: 'geojson',
    data: rewritten,
  });

  ensureClickedBuildingLayers(mapRef);
};

// Tracks which marker PNGs are mid-flight (loadImage not yet resolved) per map.
// `map.hasImage(id)` only flips true inside the async loadImage callback, so
// without this guard every rapid repeat call to ensureClickedBuildingLayers
// (prop null -> real, fetchBuildingsWithinBounds, styledata ticks during the
// load window) re-requests the same ~70 PNGs. Keyed per map (WeakMap) so two
// concurrent map instances don't starve each other's addImage, and only used
// for the in-flight window — `map.hasImage` remains the source of truth for
// "already present", so a style reload (which wipes images) correctly re-loads.
const inflightMarkerImagesByMap = new WeakMap<mapboxgl.Map, Set<string>>();
const getInflightMarkerImages = (map: mapboxgl.Map): Set<string> => {
  let set = inflightMarkerImagesByMap.get(map);
  if (!set) {
    set = new Set<string>();
    inflightMarkerImagesByMap.set(map, set);
  }
  return set;
};

/** Load seller/buyer marker images and install their layers on a ready style. */
const ensureClickedBuildingLayers = (
  mapRef: React.MutableRefObject<mapboxgl.Map | null>,
) => {
  const map = mapRef.current;
  if (!map) return;
  const sourceExists = !!map.getSource('clicked-buildings');
  if (!sourceExists) {
    console.warn('clicked-buildings source does not exist');
    return;
  }

  // `pixelRatio: 2` matches the @2x pre-rendered PNGs from
  // public/assets/markers/seller/build-seller-icons.py. `svg: true` forces
  // a canvas roundtrip; only building-dot still needs it.
  const imageSpecs: Array<{
    id: string;
    url: string;
    svg?: boolean;
    pixelRatio?: number;
  }> = [
    { id: 'building-dot', url: `${process.env.PUBLIC_URL}/assets/markers/building-dot--single--default.svg`, svg: true },
    { id: 'building-dot-selected', url: `${process.env.PUBLIC_URL}/assets/markers/building-dot--single--selected.svg`, svg: true },
    // Small white dot for low-content sell listings (decluttered maps). The
    // hover/selected state reuses the full selected pill, so no -selected dot.
    { id: 'off-market-sell-dot', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--dot--default.png`, pixelRatio: 2 },
    // Pre-generated per-count PNGs for counts 1..9. Counts >=10 are rendered
    // lazily at runtime via canvas (see ensureSellMultiIcon).
    { id: 'off-market-sell-icon-1', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--single--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-1-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--single--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--2--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--2--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--3--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--3--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--4--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--4--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--5--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--5--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--6--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--6--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--7--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--7--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--8--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--8--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--9--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller/seller--multiple--9--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-1-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--single--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-1-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--single--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-1-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--single--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--2--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--2--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--2--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--3--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--3--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--3--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--4--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--4--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--4--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--5--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--5--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--5--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--6--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--6--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--6--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--7--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--7--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--7--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--8--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--8--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--8--selected.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-new', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--9--default.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-new-seen', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--9--seen.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-new-selected', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--9--selected.png`, pixelRatio: 2 },
    // Finnish "Uusi" variants. Image ids gain a single-dash `-fi` suffix.
    { id: 'off-market-sell-icon-1-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--single--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-1-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--single--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-1-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--single--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--2--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--2--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-2-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--2--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--3--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--3--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-3-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--3--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--4--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--4--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-4-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--4--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--5--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--5--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-5-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--5--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--6--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--6--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-6-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--6--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--7--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--7--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-7-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--7--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--8--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--8--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-8-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--8--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-new-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--9--default--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-new-seen-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--9--seen--fi.png`, pixelRatio: 2 },
    { id: 'off-market-sell-icon-9-new-selected-fi', url: `${process.env.PUBLIC_URL}/assets/markers/seller-new--multiple--9--selected--fi.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-1', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--1--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-2', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--2--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-3', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--3--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-4', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--4--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-5', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--5--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-6', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--6--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-7', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--7--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-8', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--8--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-9', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--9--default.png`, pixelRatio: 2 },
    { id: 'off-market-buy-icon-10plus', url: `${process.env.PUBLIC_URL}/assets/markers/buyer--10plus--default.png`, pixelRatio: 2 },
    { id: 'heart-icon', url: `${process.env.PUBLIC_URL}/assets/heart.png` },
  ];

  // Skip images already added (hasImage) AND those a previous call is still
  // fetching (inflight) — otherwise the same PNGs get requested several times
  // before the first loadImage callback runs.
  const inflight = getInflightMarkerImages(map);
  const missingImages = imageSpecs.filter(
    ({ id }) => !map.hasImage(id) && !inflight.has(id),
  );

  // Last SVG path — only building-dot still loads as SVG. When migrated to
  // PNG, this function and the `svg: true` imageSpecs entries can be deleted.
  const loadSvgAndAdd = (id: string, url: string): Promise<void> =>
    new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';
      img.onload = () => {
        try {
          if (map.hasImage(id)) return resolve();
          const scale = 2;
          const w = (img.width || 44) * scale;
          const h = (img.height || 44) * scale;
          const canvas = document.createElement('canvas');
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext('2d');
          if (!ctx) return resolve();
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(img, 0, 0, w, h);
          const data = ctx.getImageData(0, 0, w, h);
          map.addImage(id, data, { pixelRatio: scale });
        } catch (err) {
          console.error(`Failed to rasterize SVG ${id}:`, err);
        }
        resolve();
      };
      img.onerror = (err) => {
        console.error(`Failed to load SVG ${id} from ${url}:`, err);
        resolve();
      };
      img.src = url;
    });

  if (missingImages.length > 0) {
    const loadPromises = missingImages.map(({ id, url, svg, pixelRatio }) => {
      // Mark in-flight before the async load and clear it on completion (added
      // OR failed) so a later call can retry a failed image but never piles a
      // duplicate request onto one already in progress.
      inflight.add(id);
      const clearInflight = () => inflight.delete(id);
      if (svg) return loadSvgAndAdd(id, url).finally(clearInflight);
      return new Promise<void>((resolve) => {
        map.loadImage(url, (err, image) => {
          if (!err && image && !map.hasImage(id)) {
            try {
              map.addImage(id, image, pixelRatio ? { pixelRatio } : undefined);
            } catch (addErr) {
              console.error(`Failed to add image ${id}:`, addErr);
            }
          }
          resolve();
        });
      }).finally(clearInflight);
    });

    Promise.all(loadPromises).then(() => {
      // Icons can finish while tiles/source updates make the style busy. Keep
      // layer creation pending until a ready frame instead of dropping it.
      whenMapStyleReady(map, () => addLayersInOrder(map));
    });
  } else {
    whenMapStyleReady(map, () => addLayersInOrder(map));
  }
};

/**
 * Is everything now visible already inside the area we last fetched for?
 *
 * A container resize keeps the camera still but changes the viewport, and the
 * two directions are not the same: the soft keyboard SHRINKS the view, so the
 * listings already loaded cover it and there is nothing to fetch — while
 * rotating a phone or dragging a window bigger EXPOSES map that was not covered,
 * which must still load. Containment is what tells those apart.
 */
const viewIsCovered = (
  map: mapboxgl.Map,
  last: React.MutableRefObject<{ w: number; s: number; e: number; n: number } | null>,
) => {
  const previous = last.current;
  if (!previous) return false;
  const b = map.getBounds();
  // Compared with a tolerance, not exactly. Resizing recomputes the bounds from
  // the new pixel dimensions, so an edge that did not move still comes back a
  // few floating-point ulps out, and an exact comparison reads that as newly
  // revealed map. A ten-thousandth of a degree is about ten metres — far below
  // anything a person could mean by moving the map, and far above the noise.
  const EPS = 1e-4;
  return (
    b.getWest() >= previous.w - EPS &&
    b.getEast() <= previous.e + EPS &&
    b.getSouth() >= previous.s - EPS &&
    b.getNorth() <= previous.n + EPS
  );
};

/** Is the view back to the whole area we last emitted, rather than a crop of it? */
const viewIsBackToRemembered = (
  map: mapboxgl.Map,
  last: React.MutableRefObject<{ w: number; s: number; e: number; n: number } | null>,
) => {
  const previous = last.current;
  if (!previous) return false;
  const b = map.getBounds();
  const EPS = 1e-4;
  return (
    b.getWest() <= previous.w + EPS &&
    b.getEast() >= previous.e - EPS &&
    b.getSouth() <= previous.s + EPS &&
    b.getNorth() >= previous.n - EPS
  );
};

/** Remember the area a set of emitted bounds covered. */
const rememberBounds = (
  map: mapboxgl.Map,
  last: React.MutableRefObject<{ w: number; s: number; e: number; n: number } | null>,
) => {
  const b = map.getBounds();
  last.current = { w: b.getWest(), s: b.getSouth(), e: b.getEast(), n: b.getNorth() };
};

/**
 * Has the camera itself changed since the last time we acted on a move?
 *
 * A container resize fires movestart/move/moveend without touching the camera —
 * only the viewport dimensions change — and on mobile the soft keyboard resizes
 * the layout viewport, so opening and dismissing a text field looks exactly like
 * two map movements. Comparing centre, zoom, bearing and pitch tells the two
 * apart; the ref is updated in place so the caller only has to ask.
 */
/**
 * Is a text field focused right now?
 *
 * This is what separates the on-screen keyboard from a deliberate resize. The
 * keyboard is the only thing that resizes the viewport without the user
 * touching the window, and the platform only ever raises it for a focused text
 * field — so while it is up, that field has focus. No timer: the question is
 * asked at the moment the movement settles, which is while the keyboard is
 * still on screen.
 */
const aTextFieldHasFocus = () => {
  const el = document.activeElement as HTMLElement | null;
  if (!el || !el.tagName) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable;
};

const cameraMoved = (
  map: mapboxgl.Map,
  last: React.MutableRefObject<{ lng: number; lat: number; zoom: number; bearing: number; pitch: number } | null>,
) => {
  const centre = map.getCenter();
  const now = {
    lng: centre.lng,
    lat: centre.lat,
    zoom: map.getZoom(),
    bearing: map.getBearing(),
    pitch: map.getPitch(),
  };
  const previous = last.current;
  last.current = now;
  if (!previous) return true;
  return (
    Math.abs(previous.lng - now.lng) > 1e-9 ||
    Math.abs(previous.lat - now.lat) > 1e-9 ||
    Math.abs(previous.zoom - now.zoom) > 1e-6 ||
    Math.abs(previous.bearing - now.bearing) > 1e-6 ||
    Math.abs(previous.pitch - now.pitch) > 1e-6
  );
};

const addLayersInOrder = (map: mapboxgl.Map) => {
  // Guard against a torn-down map: this runs from async loadImage callbacks
  // (Promise.all().then), which can resolve after the map was removed on
  // unmount or an HMR hot-swap — calling getLayer on a styleless map throws
  // "Cannot read properties of undefined (reading 'getOwnLayer')".
  if (!map || !map.isStyleLoaded()) return;
  const declutter = isSellDeclutterEnabled(map);
  if (!map.getLayer('building-jaakko-outline')) {
    try {
      map.addLayer({
        id: 'building-jaakko-outline',
        type: 'line',
        source: 'clicked-buildings',
        filter: ['==', ['get', 'isRecentlyCommented'], true],
        minzoom: 1,
        paint: {
          'line-color': '#F7CF3E',
          'line-width': 1.4,
          'line-opacity': 0.8,
        },
      });
    } catch (err) {
      console.error('Failed to add building-jaakko-outline layer:', err);
    }
  }

  if (!map.getLayer('building-off-market-sell-icon')) {
    try {
      map.addLayer({
        id: 'building-off-market-sell-icon',
        type: 'symbol',
        source: 'clicked-buildings',
        // Decluttered maps keep the pill for full-tier buildings only; the
        // dot tier renders through building-off-market-sell-dot below.
        filter: declutter
          ? ['all',
              ['>', ['to-number', ['get', 'could_sell_count'], 0], 0],
              // Dot-tier buildings join the pill layer once the clickable
              // building dots appear — at that zoom a reduced dot hides
              // information for no gain. Zoom in a filter is only evaluated
              // at integer zoom levels, which is exactly where the dot
              // layer's maxzoom hands over.
              ['any',
                ['!', sellTierIsDot],
                ['>=', ['zoom'], BUILDING_DOTS_MIN_ZOOM],
              ],
            ]
          : ['>', ['to-number', ['get', 'could_sell_count'], 0], 0],
        minzoom: 1,
        layout: {
          'icon-image': sellIconImageExpr(false, currentIconLocale),
          'icon-size': iconSizeExpr,
          'icon-anchor': 'center',
          'icon-offset': iconOffsetExpr(1),
          // Decluttering: let Mapbox collision hide overlapping pills, the
          // best-ranked (lowest sell_priority) surviving. Zooming in spreads
          // the features apart, so hidden pills fade back in automatically.
          // From the building-dots zoom on, collision is released entirely
          // (pre-decluttering behavior): at building-inspection zoom every
          // sell listing must be visible, overlap or not.
          'icon-allow-overlap': declutter
            ? expr(['step', ['zoom'], false, BUILDING_DOTS_MIN_ZOOM, true])
            : true,
          ...(declutter
            ? {
                // Padding steps back to the Mapbox default above the
                // threshold so pills don't suppress base-map labels any more
                // aggressively than they did before decluttering.
                'icon-padding': expr([
                  'step', ['zoom'],
                  SELL_PILL_COLLISION_PADDING,
                  BUILDING_DOTS_MIN_ZOOM, 2,
                ]),
                'symbol-sort-key': sellPrioritySortKey,
              }
            : {}),
        },
        paint: {
          'icon-opacity': iconOpacityHover(false),
        },
      });
    } catch (err) {
      console.error('Failed to add building-off-market-sell-icon layer:', err);
    }
  }

  if (!map.getLayer('building-off-market-sell-icon-selected')) {
    try {
      map.addLayer({
        id: 'building-off-market-sell-icon-selected',
        type: 'symbol',
        source: 'clicked-buildings',
        // Deliberately covers ALL sell buildings (dot tier included): hovering
        // or selecting a dot swaps it to the full selected pill via the
        // feature-state opacity pair.
        filter: ['>', ['to-number', ['get', 'could_sell_count'], 0], 0],
        minzoom: 1,
        layout: {
          'icon-image': sellIconImageExpr(true, currentIconLocale),
          'icon-size': iconSizeExpr,
          'icon-anchor': 'center',
          'icon-offset': iconOffsetExpr(1),
          'icon-allow-overlap': true,
          // Decluttering: placement runs top-down, so this always-placed
          // overlay would otherwise write a collision box at every sell
          // building and cull the entire base pill layer beneath it.
          ...(declutter ? { 'icon-ignore-placement': true } : {}),
        },
        paint: {
          'icon-opacity': iconOpacityHover(true),
        },
      });
    } catch (err) {
      console.error('Failed to add building-off-market-sell-icon-selected layer:', err);
    }
  }

  // Every sell building has a small dot below the pill layer. A full-tier
  // building whose pill loses a collision still has its dot; dot-tier
  // buildings show only the dot. All become pills at building zoom. Dots do
  // not reserve collision space for either pills or buying-interest coins.
  // They remain clickable/hoverable through the same building preview.
  if (declutter && !map.getLayer('building-off-market-sell-dot')
      && map.getLayer('building-off-market-sell-icon')) {
    try {
      map.addLayer({
        id: 'building-off-market-sell-dot',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: ['>', ['to-number', ['get', 'could_sell_count'], 0], 0],
        minzoom: 1,
        // Hidden from the building-dots zoom on: the pill layer's zoom-aware
        // filter takes over the dot tier there (layers hide at zoom >=
        // maxzoom, so the handoff is exact — no gap, no double-render).
        maxzoom: BUILDING_DOTS_MIN_ZOOM,
        layout: {
          'icon-image': 'off-market-sell-dot',
          'icon-size': iconSizeExpr,
          'icon-anchor': 'center',
          // Same offset as the pill so the hover swap (dot -> selected pill)
          // and the buy-stack separation behave identically across tiers.
          'icon-offset': iconOffsetExpr(1),
          'icon-allow-overlap': true,
          'icon-ignore-placement': true,
          'symbol-sort-key': sellPriorityDrawKey,
        },
        paint: {
          'icon-opacity': iconOpacityHover(false),
        },
      }, 'building-off-market-sell-icon');
    } catch (err) {
      console.error('Failed to add building-off-market-sell-dot layer:', err);
    }
  }

  if (!map.getLayer('building-off-market-buy-icon')) {
    try {
      map.addLayer({
        id: 'building-off-market-buy-icon',
        type: 'symbol',
        source: 'clicked-buildings',
        filter: ['>', ['to-number', ['get', 'could_buy_count'], 0], 0],
        // Deep street zoom only: the interest stage coins
        // (demandLayers.ts) represent buying interest up to COIN_SWITCH_ZOOM.
        // The per-building stacks only start fading in AFTER the stage coins
        // are fully gone, so the two never pile up in the same view.
        minzoom: COIN_SWITCH_ZOOM,
        layout: {
          'icon-image': expr([
            'step',
            ['to-number', ['get', 'could_buy_count'], 0],
            'off-market-buy-icon-1',
            2, 'off-market-buy-icon-2',
            3, 'off-market-buy-icon-3',
            4, 'off-market-buy-icon-4',
            5, 'off-market-buy-icon-5',
            6, 'off-market-buy-icon-6',
            7, 'off-market-buy-icon-7',
            8, 'off-market-buy-icon-8',
            9, 'off-market-buy-icon-9',
            10, 'off-market-buy-icon-10plus',
          ]),
          'icon-size': iconSizeExpr,
          'icon-anchor': 'center',
          'icon-offset': iconOffsetExpr(-1),
          'icon-allow-overlap': true,
          // Decluttering: the buy stacks render above the sell layers and so
          // place first — without this they'd randomly cull adjacent sell
          // pills/dots at street zoom. Renders the stacks exactly as before;
          // only stops them from blocking other symbols' placement.
          ...(declutter ? { 'icon-ignore-placement': true } : {}),
        },
        paint: {
          'icon-opacity': expr(['interpolate', ['linear'], ['zoom'], COIN_SWITCH_ZOOM, 0, COIN_SWITCH_ZOOM + 1, 1]),
        },
      });
    } catch (err) {
      console.error('Failed to add building-off-market-buy-icon layer:', err);
    }
  }
};

export default BuildingMap;
